/**
 * Dialer — in-browser calling via the Twilio Voice JS SDK.
 *
 * Thin wrapper around a server-rendered, persistent "dialer dock" that lives
 * OUTSIDE #page-content (rendered in {% block post_bundle_js %}), so it survives
 * HTMX boosted navigation. We deliberately do NOT destroy on htmx:beforeSwap.
 *
 * Server contract:
 *   GET  /telephony/token/                  -> 200 {token, identity} | 403
 *   GET  /telephony/calls/lines/            query: contact_phone_pk, lead_pk | dispo_lead_pk (acqs only)
 *                                           -> 200 {lines: [{id, label}]}, start line first
 *   POST /telephony/calls/start/            form: contact_phone_pk, lead_pk | dispo_lead_pk, line_id? (acqs)
 *                                                 OR leasing_lead_pk (leasing)
 *                                                 OR occupancy_pk, phone, call_context (tenant)
 *                                                 + always mic_label, speaker_label, client_info (JSON)
 *                                           -> 200 {callAttemptId, outcomes: [{value, label}]} | 403/404/400
 *                                              (acqs also: contactId, makeMainOutcomes)
 *   POST /telephony/calls/<id>/outcome/     form: outcome, outcome_notes, make_main?
 *                                           -> 200 {ok:true}
 *   POST /telephony/calls/<id>/diagnostics/ form: csrfmiddlewaretoken, client_events (JSON), input_levels (JSON)
 *                                           -> 200 {ok, events, levels} | 400/404
 *                                              (once at call end; sendBeacon on pagehide mid-call)
 *   POST /telephony/client-error/           form: stage (device | dial | mic), code, message, attempt_id?, final?
 *                                           -> 200 {reachedTwilio: bool|null} | 404
 *                                              (logs a browser Twilio error; final=1 marks a
 *                                              never-placed attempt failed and pages on-call;
 *                                              stage=mic is a silent-mic spell, log only)
 * After calls/start: device.connect({ params: { CallAttemptId } }). Twilio hits
 * the server's TwiML endpoint itself — we never call it.
 *
 * Reuse endpoints (acqs — same-origin POST, CSRF):
 *   POST /acqs/contact-phones/<pk>/update/  form: status=wrong_number|out_of_service
 *   POST /acqs/leads/<pk>/next-action/      form: next_action, next_action_date
 *
 * The dock is a full calling surface: mute, DTMF keypad, in-call notes,
 * single-select outcome chips (built from the `outcomes` set calls/start
 * returns for this call), and (when a lead is in context) next-action controls. On hang-up it
 * enters WRAP-UP mode — it keeps whatever notes / outcome / next-action the
 * rep set during the call and prompts only for what's missing, then posts
 * everything once on Save.
 *
 * Device panel (Iteration 7, Loop #1427): EVERY call opens an `is-preview`
 * panel before it is placed — the rep confirms + tests mic/speaker/ringer, then
 * Dial (focused, so Enter dials) places it; Cancel goes back to idle with no
 * CallAttempt created.  Choices persist in localStorage['dialer.audioDevices'].
 * A gear in the controls re-opens the same panel any time (incl. mid-call).
 * All client-side via Twilio's device.audio (AudioHelper) — no server
 * preference; device IDs are browser-origin-scoped.
 *
 * In-call device row + silent-mic check (Loop #1427): during a live call the
 * dock shows a mic picker, a mic bar fed by the Call `volume` event (the stream
 * actually sent), and a speaker picker; picking a device from a picker's menu
 * switches the live call.  Input under MIC_SILENT_LEVEL for MIC_SILENT_MS on an
 * answered, unmuted call, or a Twilio `constant-audio-input-level` warning, shows
 * the mic banner ("Switch mic" opens the mic picker's menu) and reports stage=mic
 * to client-error at the start and end of the spell.  The console logs the input once a second.
 * The call's audio diagnostics (events and per-second input levels) are
 * buffered and posted to calls/<id>/diagnostics/ when the call ends.
 *
 * From switch: before an acqs call the dock fetches calls/lines.  With two or
 * more lines the device panel shows a "From" select at its top (start line
 * selected) and Dial posts the choice as line_id; with one line, or when the
 * fetch fails, there is no select and the call goes out on the start line.  The
 * header shows "From: <line>" once the line is settled.  Leasing and tenant
 * calls, and the gear, have no From select.
 *
 * Call triggers (delegated, anywhere on the page):
 *   acqs:    [data-dialer-call][data-contact-phone-pk][data-lead-pk | data-dispo-lead-pk][data-dialer-label]
 *   leasing: [data-dialer-call][data-leasing-lead-pk][data-dialer-label]
 *   tenant:  [data-dialer-call][data-occupancy-pk][data-tenant-phone?][data-call-context?][data-dialer-label]
 *
 * Usage: Dialer.init() — auto-invoked on DOMContentLoaded.
 */
import { Device } from '@twilio/voice-sdk';

var Dialer = (function() {
    'use strict';

    var dock = null;
    var statusEl = null;
    var contextEl = null;
    var timerEl = null;
    var hangupBtn = null;
    var muteBtn = null;
    var keypadToggleBtn = null;
    var keypadEl = null;
    var notesEl = null;
    var outcomesEl = null;            // outcome chip container, filled from calls/start `outcomes`
    var nextActionWrap = null;
    var nextActionTextEl = null;
    var wrapupHintEl = null;
    var saveBtn = null;
    var dismissBtn = null;

    // ── Audio-device preview / setup (Iteration 7) ──
    var settingsBtn = null;       // gear in the controls — re-opens the device panel
    var previewUnavailableEl = null;
    var micSelect = null;
    var speakerSelect = null;
    var ringerSelect = null;
    var meterFillEl = null;
    var outputFieldEls = null;    // NodeList — speaker + ringer field rows
    var outputNoteEl = null;      // "uses system default speaker" fallback note
    var speakerTestBtn = null;
    var previewDialBtn = null;
    var previewCancelBtn = null;

    // ── From switch (acqs calls) ──
    var fromEl = null;                // header "From: <line>" line
    var lineFieldEl = null;           // the From field at the top of the device panel
    var lineSelect = null;            // its <select>
    var callLines = [];               // [{id, label}] from calls/lines, start line first
    var activeLineId = null;          // the line this acqs call goes out on (posted as line_id)
    var lineFetchSeq = 0;             // bumped per fetch; a superseded fetch's answer is ignored

    // ── Compliance warn surface (Iteration 4) ──
    var complianceTextEl = null;      // the warning message span
    var complianceCallBtn = null;     // "Call anyway" — re-POSTs override=true
    var complianceCancelBtn = null;   // Cancel / Close — back to idle
    var badNumberWrap = null;         // Bad number's Wrong number / Out of service / Do not call chips
    var followUpWrap = null;          // Follow up's date chips and date input
    var handOffWrap = null;           // Hand off's person select
    var nextTextWrap = null;          // next-action text, for Follow up and Hand off
    var nextDateEl = null;            // Follow up's date input
    var handOffPersonEl = null;       // Hand off's person select element
    var makeMainWrap = null;          // "Make this the main number" field (acqs calls only)
    var makeMainEl = null;            // its checkbox

    // ── Network status banner (Iteration 6a) ──
    var netBannerEl = null;           // the reconnecting / dropped banner
    var netBannerIconEl = null;       // icon (spinner while reconnecting, plug-x on drop)
    var netBannerTextEl = null;       // banner message span
    var netTrouble = false;           // call is in a network-trouble state (reconnecting / signaling error)

    var STORAGE_KEY = 'dialer.audioDevices';
    var inputVolumeAudio = null;         // the AudioHelper the inputVolume listener is on — a rebuilt Device has a new one
    var panelVisible = false;            // gates the mic-meter repaint (no idle work)
    var pendingCall = null;              // {contactPhonePk, leadPk, dispoLeadPk, label} stashed while in preview —
                                         // only a "call pending" marker; proceedToDial reads the active* state
    var previewMode = null;              // 'dial' (pending call) | 'settings' (gear, no call)
    var savedContext = '';               // header context/status snapshotted when the gear opens
    var savedStatus = '';                // settings mid-call/wrap-up, restored on Close/Cancel

    var device = null;
    var activeCall = null;
    var callMicError = null;     // why applyCallInputDevice could not take the mic (see micError)
    var activeAttemptId = null;  // CallAttempt PK of the in-flight call (for the answer match)
    var activeLabel = '';        // contact label shown in the dock status line
    var activeContactPhonePk = null;  // called phone — target of Bad number's status POST
    var activeLeadPk = null;          // for the next-action POST (null on pure contact surfaces)
    var activeDispoLeadPk = null;     // buyer lead (AcqDispoLead) pk — the call logs there; no next-action
    var activeLeasingLeadPk = null;   // leasing-call target (LeasingLead pk)
    var activeOccupancyPk = null;     // tenant-call target (Occupancy pk) — activeLeasingLeadPk,
                                       // activeOccupancyPk and contactPhonePk are mutually exclusive
    var activeTenantPhone = null;     // optional raw phone posted alongside occupancy_pk
    var activeCallContext = null;     // optional call_context (e.g. 'collections') posted alongside occupancy_pk
    var selectedOutcome = null;       // one of the outcome strings, set by chip click
    var activeContactId = null;       // AcqContact pk from calls/start (acqs only) — contactUpdated payload
    var makeMainOutcomes = [];        // outcomes that offer "Make this the main number" (calls/start; [] off acqs)
    var nextActionDays = null;        // quick-date chip selection (days from today)
    var selectedPhoneStatus = null;   // the chip under Bad number; Save sets the called phone's status
    var nextStep = null;              // follow_up | book | hand_off | none
    var nextStepInfo = null;          // calls/<pk>/next-step/: {hasNextAction, bookUrl, people}; null until it loads
    var wrapup = false;               // dock is in post-call wrap-up mode
    var dialFailed = false;           // the call errored out: its wrap-up may Skip or Save blank

    var initialized = false;
    var deviceReady = false;

    // ── Device self-healing (see setupDevice) ──
    var REPORT_URL = '/telephony/client-error/';
    var TOKEN_MAX_AGE_MS = 50 * 60 * 1000;  // tokens live 3600s; rebuild before that on wake
    var DIAL_QUEUE_MS = 20000;              // how long a click waits for the Device to come back
    var DIAL_WATCHDOG_MS = 30000;           // how long a dial may go without reaching Twilio
    var tokenUrl = null;
    var tokenFetchedAt = 0;
    var settingUp = false;            // a token fetch / registration is in flight
    var setupTimer = null;            // releases settingUp if setup hangs (SETUP_TIMEOUT_MS)
    var setupGen = 0;                 // bumped per setup; a token fetch from an abandoned setup is ignored
    var SETUP_TIMEOUT_MS = 15000;
    var rebuildTimer = null;
    var rebuildFailures = 0;          // consecutive rebuilds without a registration — drives backoff
    var lastDeviceError = null;       // most recent Device/token error, for the give-up report
    var queuedDial = null;            // {args, at} — a call clicked while the Device was down
    var queueTimer = null;
    var dialInFlight = false;         // connecting a call that hasn't reached Twilio yet
    var dialRetried = false;          // this attempt already got its one rebuild-and-retry
    var dialWatchdog = null;
    var timerInterval = null;
    var timerStart = null;

    // ── Logging (never fail silently — explain what happened) ──

    function log() {
        console.log.apply(console, ['[dialer]'].concat([].slice.call(arguments)));
    }
    function warn() {
        console.warn.apply(console, ['[dialer]'].concat([].slice.call(arguments)));
    }

    // ── DOM helpers ──

    function setStatus(text) {
        if (statusEl) statusEl.textContent = text;
    }

    // Device-health status (Connecting / Reconnecting / offline) — only when the dock isn't busy
    // with a call, wrap-up, a dial in flight, or the audio-setup panel, whose own status wins.
    function setIdleStatus(text) {
        if (!activeCall && !wrapup && !dialInFlight && !previewMode) setStatus(text);
    }

    function setContext(text) {
        if (contextEl) contextEl.textContent = text || 'Dialer';
    }

    // ── In-call timer ──

    function formatElapsed(seconds) {
        var h = Math.floor(seconds / 3600);
        var m = Math.floor((seconds % 3600) / 60);
        var s = seconds % 60;
        var mm = String(m).padStart(2, '0');
        var ss = String(s).padStart(2, '0');
        return h > 0 ? (h + ':' + mm + ':' + ss) : (m + ':' + ss);
    }

    function startTimer() {
        stopTimer(); // always clear any stale interval first
        timerStart = Date.now();
        if (timerEl) {
            timerEl.textContent = '0:00';
            timerEl.hidden = false;
        }
        timerInterval = setInterval(function() {
            var elapsed = Math.floor((Date.now() - timerStart) / 1000);
            if (timerEl) timerEl.textContent = formatElapsed(elapsed);
        }, 1000);
    }

    function stopTimer() {
        if (timerInterval !== null) {
            clearInterval(timerInterval);
            timerInterval = null;
        }
        timerStart = null;
        if (timerEl) {
            timerEl.textContent = '';
            timerEl.hidden = true;
        }
    }

    // ── Ringback ──
    //
    // The carrier's early media is often near-silence until voicemail picks up,
    // so the rep heard dead air for the whole ring. Play a US ringback (440+480 Hz,
    // 2s on / 4s off) from the first `ringing` until the call answers or ends, on
    // the same speaker the call audio uses. The SDK's own "outgoing" sound is off
    // (see `registered`) — it plays on ANSWER, which reps heard as a late ring.
    var ringbackEl = null;
    var ringbackSrc = null;

    function ringbackUrl() {
        if (ringbackSrc) return ringbackSrc;
        var rate = 8000, seconds = 6, onSamples = 2 * rate, n = seconds * rate;
        var buf = new ArrayBuffer(44 + n * 2);
        var v = new DataView(buf);
        function str(off, s) { for (var i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); }
        str(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); str(8, 'WAVE');
        str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
        v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true);
        v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, n * 2, true);
        for (var s = 0; s < onSamples; s++) {
            var t = s / rate;
            var sample = 0.2 * (Math.sin(2 * Math.PI * 440 * t) + Math.sin(2 * Math.PI * 480 * t));
            v.setInt16(44 + s * 2, Math.round(sample * 32767), true);
        }
        ringbackSrc = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
        return ringbackSrc;
    }

    function startRingback(call) {
        if (ringbackEl || ringbackHeard) return;  // `ringing` fires twice (with and without early media)
        ringbackEl = new Audio(ringbackUrl());
        ringbackEl.loop = true;
        var speakerId = (device && device.audio) ? firstOutputId(device.audio.speakerDevices) : 'default';
        if (outputSelectionSupported() && speakerId !== 'default' && ringbackEl.setSinkId) {
            ringbackEl.setSinkId(speakerId).catch(function() {});
        }
        ringbackEl.play().catch(function(err) {
            warn('ringback could not play —', (err && err.message) || err);
        });
        watchEarlyMedia(call);
    }

    // Carriers that DO send a ring (or a "not in service" message) as early media
    // would play on top of ours, a couple of seconds out of phase. Watch the
    // call's incoming audio and hand over to the carrier the moment it carries
    // sound (~300ms above -40 dBFS). Silent early media leaves our ring playing.
    // If Web Audio can't read the stream, ours just keeps playing.
    var ringbackHeard = false;   // carrier sound seen — don't restart ours this call
    var ringbackWatch = null;    // { ctx, timer }

    function watchEarlyMedia(call) {
        var Ctx = window.AudioContext || window.webkitAudioContext;
        if (!call || !Ctx || ringbackWatch) return;
        var ctx, analyser, samples, loudTicks = 0;
        try {
            ctx = new Ctx();
            ctx.resume().catch(function() {});
        } catch (err) {
            return;
        }
        ringbackWatch = { ctx: ctx, timer: null };
        ringbackWatch.timer = window.setInterval(function() {
            if (!analyser) {
                // The remote stream exists only once early media is negotiated.
                var stream = call.getRemoteStream && call.getRemoteStream();
                if (!stream || !stream.getAudioTracks().length) return;
                try {
                    analyser = ctx.createAnalyser();
                    analyser.fftSize = 1024;
                    ctx.createMediaStreamSource(stream).connect(analyser);
                    samples = new Float32Array(analyser.fftSize);
                } catch (err) {
                    warn('ringback: cannot read early media —', (err && err.message) || err);
                    stopEarlyMediaWatch();
                }
                return;
            }
            analyser.getFloatTimeDomainData(samples);
            var sum = 0;
            for (var i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
            loudTicks = Math.sqrt(sum / samples.length) > 0.01 ? loudTicks + 1 : 0;
            if (loudTicks >= 3) {
                log('carrier early media has sound — stopping our ringback');
                ringbackHeard = true;
                stopRingback();
            }
        }, 100);
    }

    function stopEarlyMediaWatch() {
        if (!ringbackWatch) return;
        window.clearInterval(ringbackWatch.timer);
        ringbackWatch.ctx.close().catch(function() {});
        ringbackWatch = null;
    }

    function stopRingback() {
        stopEarlyMediaWatch();
        if (!ringbackEl) return;
        ringbackEl.pause();
        ringbackEl = null;
    }

    function showDock() {
        if (dock) dock.classList.remove('is-idle');
    }

    function hideDock() {
        if (dock) {
            dock.classList.add('is-idle');
            dock.classList.remove('is-active');
            dock.classList.remove('is-wrapup');
            dock.classList.remove('is-preview');
            dock.classList.remove('is-compliance');
        }
    }

    // Active = a live Twilio call is in progress. Enables in-call controls.
    function setActive(active) {
        if (!dock) return;
        dock.classList.toggle('is-active', active);
        if (hangupBtn) hangupBtn.disabled = !active;
        if (muteBtn) muteBtn.disabled = !active;
        if (keypadToggleBtn) keypadToggleBtn.disabled = !active;
    }

    // ── Toast (reuse SeeMe if present) ──

    function toast(message, type) {
        if (window.SeeMe && window.SeeMe.toast && window.SeeMe.toast[type || 'success']) {
            window.SeeMe.toast[type || 'success'](message);
        } else {
            log('toast:', type || 'success', '—', message);
        }
    }

    // ── Microphone errors ──
    //
    // Map a failure to acquire the mic to what the rep sees, or null when it
    // isn't a mic failure we recognise. `message` is the toast; `status` is the
    // short form for the dock status line, which truncates past ~30 characters.
    // Takes either a getUserMedia DOMException (our direct call, or a
    // device.audio.setInputDevice rejection, which calls getUserMedia inside the
    // SDK) or a Twilio Call error:
    //   31401 PermissionDeniedError  — the SDK's getUserMedia got NotAllowedError
    //   31402 AcquisitionFailedError — any OTHER getUserMedia failure. The SDK
    //         drops the original DOMException, so 31402 alone can't tell "in use"
    //         from "no mic"; callers pass the raw error first when they have one.
    // "In use": Chrome NotReadableError ("Could not start audio source"), old
    // Chrome TrackStartError, and AbortError (Firefox: the source could not start).
    var MIC_ERRORS = {
        blocked: { status: 'Microphone blocked',
                   message: "Microphone access was blocked — allow it in your browser's site settings to place calls" },
        missing: { status: 'No microphone found', message: 'No microphone found' },
        busy:    { status: 'Microphone in use', message: 'Microphone is in use by another app' },
    };
    function micError(err) {
        if (!err) return null;
        var name = err.name;
        var code = err.code;
        if (name === 'NotAllowedError' || name === 'SecurityError' ||
            name === 'PermissionDeniedError' || code === 31401) {
            return MIC_ERRORS.blocked;
        }
        if (name === 'NotFoundError' || name === 'OverconstrainedError' ||
            name === 'DevicesNotFoundError') {
            return MIC_ERRORS.missing;
        }
        if (name === 'NotReadableError' || name === 'TrackStartError' ||
            name === 'AbortError' || code === 31402) {
            return MIC_ERRORS.busy;
        }
        return null;
    }

    // ── Network status banner (Iteration 6a) ──
    //
    //  A live call can lose its connection two ways:
    //   • a transient blip — the Twilio Call fires `reconnecting` then
    //     `reconnected` once media/signaling recovers (mobile airplane-mode
    //     recovered in ~5–10s). Show "Connection lost — reconnecting…" (warning
    //     tone) and clear it on recovery — the call resumes, don't hang up.
    //   • a hard drop — the signaling websocket closes (1006 → ConnectionError
    //     31005) and the call can't recover. WITHOUT this, dialer.js saw only a
    //     `disconnect` and slid silently into the normal wrap-up, as if the rep
    //     had hung up. Show "Call dropped — network" (error tone) so the drop is
    //     visible; the banner stays up through wrap-up so the rep still logs it.

    // Codes that mean "the network/transport failed", NOT a clean hangup:
    //   31000 gateway "General Error" — the Call-side report when the gateway tears
    //         the call down after the connection is lost (the observed wifi-drop end)
    //   31005 ConnectionError (signaling websocket close 1006 — the wifi-drop case)
    //   31009 Transport error
    //   53000 Signaling connection error / 53001 Signaling connection disconnected
    //   53405 Media connection failed
    function isNetworkError(code) {
        return code === 31000 || code === 31005 || code === 31009 ||
               code === 53000 || code === 53001 || code === 53405;
    }

    // The far end refused or never picked up — the gateway HANGUPs the browser leg
    // with a 31xxx code built from the SIP response. Without
    // `enableImprovedSignalingErrorPrecision` on the Device the SDK collapses the
    // first four into 31005, and a code it has no class for at all (31487 — the
    // ring timed out) becomes 31005 even with the flag. isNetworkError() reads
    // 31005 as "Call dropped — network", so farEndStatus() runs first.
    var FAR_END_ERRORS = {
        31404: 'Number not found',
        31408: 'No answer',
        31480: 'No answer — phone unavailable',
        31486: 'Busy',
        31487: 'No answer',
        31603: 'Call declined'
    };

    // The wrap-up status for a far-end hangup, or null. The SDK's fallback for an
    // unknown gateway code is ConnectionError('Error sent from gateway in HANGUP')
    // with the gateway's own error as `originalError`; a real transport drop never
    // carries one.
    function farEndStatus(err) {
        var code = err && err.code;
        if (FAR_END_ERRORS[code]) return FAR_END_ERRORS[code];
        var gatewayCode = Number(err && err.originalError && err.originalError.code);
        if (code === 31005 && gatewayCode) {
            return FAR_END_ERRORS[gatewayCode] || 'Not connected (' + gatewayCode + ')';
        }
        return null;
    }

    function setNetBanner(text, dropped) {
        if (netBannerTextEl) netBannerTextEl.textContent = text;
        if (netBannerEl) {
            netBannerEl.classList.toggle('is-dropped', !!dropped);
            netBannerEl.hidden = false;
        }
        syncMicBanner();  // the network banner wins
        // Spinner while reconnecting; a struck plug once the call has dropped.
        if (netBannerIconEl) {
            netBannerIconEl.className = dropped
                ? 'fa-solid fa-plug-circle-xmark'
                : 'fa-solid fa-arrows-rotate fa-spin';
        }
    }

    function hideNetBanner() {
        netTrouble = false;
        if (netBannerEl) {
            netBannerEl.hidden = true;
            netBannerEl.classList.remove('is-dropped');
        }
        syncMicBanner();  // an open silent spell's banner comes back
    }

    // Transient blip (or first sign of trouble) — optimistic: the SDK may still
    // recover. clearReconnecting() restores the live-call status on `reconnected`;
    // showDropped() takes over if the call ultimately disconnects.
    function showReconnecting() {
        netTrouble = true;
        setNetBanner('Connection lost — reconnecting…', false);
        setStatus('Reconnecting…');
    }

    function clearReconnecting() {
        netTrouble = false;
        hideNetBanner();
        // Back to the live-call status (timer running once answered → In call).
        setStatus(timerInterval !== null ? 'In call' : 'Calling…');
    }

    // Hard drop — the call is gone for network reasons. Keep the banner up; the
    // disconnect handler then enters wrap-up so the rep can still log it.
    function showDropped() {
        netTrouble = false;
        setNetBanner('Call dropped — network', true);
        setStatus('Call dropped');
    }

    // ════════════════════════════════════════════════════════════════════
    //  Audio-device setup / preview (Iteration 7)
    //
    //  Every call routes through an `is-preview` state — the rep confirms +
    //  tests mic/speaker/ringer (and, on a multi-line acqs call, the From
    //  line) before the call is placed; device choices persist in
    //  localStorage. The gear re-opens the panel any time (incl. mid-call;
    //  device changes apply live and persist).
    //
    //  Device API is Twilio's device.audio (AudioHelper):
    //    availableInputDevices / availableOutputDevices  Map<deviceId, MediaDeviceInfo>
    //    setInputDevice(id) -> Promise            (mic, works mid-call)
    //    speakerDevices.set(id) / .test()         (call audio out; test tone)
    //    ringtoneDevices.set(id)                  (ringing out)
    //    isOutputSelectionSupported               (Chrome/Edge only)
    //    on('deviceChange') / on('inputVolume')
    // ════════════════════════════════════════════════════════════════════

    function readSavedDevices() {
        try {
            var raw = window.localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            warn('could not read saved audio devices —', e && e.message);
            return null;
        }
    }

    function writeSavedDevices(obj) {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
        } catch (e) {
            warn('could not persist audio devices —', e && e.message);
        }
    }

    // Resolve a microphone deviceId that is GUARANTEED to exist in the SDK's
    // availableInputDevices map, or null when there are no inputs at all. This is
    // the single source of truth for "which mic id may we hand the SDK".
    //
    // Cross-browser: Chromium exposes a literal 'default' input; Safari/Firefox
    // do NOT — they only expose real (opaque) deviceIds. Passing 'default' to
    // setInputDevice on Safari throws "Device not found: default". So:
    //   1) the saved input, IF it's in the available map;
    //   2) else 'default', ONLY when the map actually contains it (Chromium);
    //   3) else the first real id in the map (Safari/Firefox);
    //   4) else null (no mic).
    function resolveInputId() {
        if (!device || !device.audio) return null;
        var map = device.audio.availableInputDevices;
        if (!map || map.size === 0) return null;
        var saved = readSavedDevices();
        if (saved && saved.input && map.has(saved.input)) return saved.input;
        if (map.has('default')) return 'default';
        return map.keys().next().value || null;
    }

    function outputSelectionSupported() {
        return !!(device && device.audio && device.audio.isOutputSelectionSupported &&
                  device.audio.availableOutputDevices &&
                  device.audio.availableOutputDevices.size > 0);
    }

    // Rebuild one <select> from a Map<deviceId, MediaDeviceInfo>. Preserves the
    // current selection if it still exists. `fallbackLabel` names the blank-label
    // default entry.
    function fillSelect(selectEl, devicesMap, activeId, fallbackLabel) {
        if (!selectEl) return;
        selectEl.innerHTML = '';
        devicesMap.forEach(function(info, id) {
            var opt = document.createElement('option');
            opt.value = id;
            opt.textContent = info.label || fallbackLabel;
            selectEl.appendChild(opt);
        });
        if (activeId && devicesMap.has(activeId)) {
            selectEl.value = activeId;
        }
    }

    // The active input device id, or 'default' when none is explicitly set.
    function activeInputId() {
        if (!device || !device.audio) return 'default';
        var inDev = device.audio.inputDevice;
        return (inDev && inDev.deviceId) || 'default';
    }

    // The first selected output device id for a collection, or 'default'.
    function firstOutputId(collection) {
        if (!collection) return 'default';
        var set = collection.get();  // Set<MediaDeviceInfo>
        var first = set && set.values().next().value;
        return (first && first.deviceId) || 'default';
    }

    function populateDevices() {
        if (!device || !device.audio) return;
        var audio = device.audio;
        fillSelect(micSelect, audio.availableInputDevices, activeInputId(), 'Default microphone');
        if (outputSelectionSupported()) {
            fillSelect(speakerSelect, audio.availableOutputDevices,
                       firstOutputId(audio.speakerDevices), 'Default speaker');
            fillSelect(ringerSelect, audio.availableOutputDevices,
                       firstOutputId(audio.ringtoneDevices), 'Default ringer');
        }
    }

    // Toggle the output (speaker/ringer/test) controls based on browser support.
    function applyOutputSupportUi() {
        var supported = outputSelectionSupported();
        if (outputFieldEls) {
            outputFieldEls.forEach(function(el) { el.hidden = !supported; });
        }
        if (outputNoteEl) outputNoteEl.hidden = supported;
    }

    // Persist all three current selections under STORAGE_KEY.
    function persistSelections() {
        if (!device || !device.audio) return;
        var obj = { input: activeInputId() };
        if (outputSelectionSupported()) {
            obj.output = firstOutputId(device.audio.speakerDevices);
            obj.ringer = firstOutputId(device.audio.ringtoneDevices);
        }
        writeSavedDevices(obj);
    }

    // Apply a single device change defensively: on failure, soft-toast and
    // revert the <select> to the still-active device — never throw into the
    // call path.
    function applyInputDevice(id, selectEl) {
        if (!device || !device.audio) return;
        // Mid-call, hold off checkCallMic's re-acquire until this switch lands, or
        // it could put the call back on the old saved mic.
        var holdRecovery = !!activeCall && !micRecovering;
        var holdCall = activeCall;
        if (holdRecovery) micRecovering = true;
        device.audio.setInputDevice(id).then(function() {
            persistSelections();
            if (activeCall) recordCallEvent('mic-switch', { mic: micLabel() });
        }).catch(function(err) {
            warn('could not set microphone —', (err && err.message) || err);
            var mic = micError(err);
            toast(mic ? mic.message : 'Could not switch microphone', 'error');
            if (selectEl) selectEl.value = activeInputId();
        }).then(function() {
            // Only for the call it held: a switch that settles after hang-up must
            // not clear the next call's recovery state.
            if (holdRecovery && activeCall === holdCall) { micRecovering = false; micDeadTicks = 0; }
            paintDeviceRow();
        });
    }

    function applyOutputDevice(collection, id, selectEl, label) {
        if (!collection) return;
        collection.set(id).then(function() {
            persistSelections();
            var info = device.audio.availableOutputDevices.get(id);
            if (activeCall) recordCallEvent(label + '-switch', { device: (info && info.label) || id });
        }).catch(function(err) {
            warn('could not set ' + label + ' —', (err && err.message) || err);
            toast('Could not switch ' + label, 'error');
            if (selectEl) selectEl.value = firstOutputId(collection);
        }).then(paintDeviceRow);
    }

    // Re-apply saved selections (after permission + populate, and after a
    // hot-plug). Missing devices fall back to default. Best-effort, silent —
    // this runs off the call path.
    //
    // INPUT (mic) is intentionally NOT restored here. setInputDevice() calls
    // getUserMedia internally, which would light the browser's "mic in use"
    // indicator on every page load — violating the Iteration-7 rule: never
    // request mic on page load. The saved mic is applied lazily by
    // activateMicForPreview() when the rep opens the setup panel or dials.
    // Output devices (speaker / ringer) are safe to restore here because
    // speakerDevices.set() / ringtoneDevices.set() do NOT call getUserMedia.
    function restoreSavedSelections() {
        if (!device || !device.audio) return;
        var saved = readSavedDevices();
        if (!saved) return;
        var audio = device.audio;
        if (outputSelectionSupported()) {
            // Only assert ids that actually exist in the available map — passing
            // an id the SDK doesn't know rejects (and on Safari there is no
            // synthetic 'default' output, so don't special-case it).
            if (saved.output && audio.availableOutputDevices.has(saved.output)) {
                audio.speakerDevices.set(saved.output).catch(function() {});
            }
            if (saved.ringer && audio.availableOutputDevices.has(saved.ringer)) {
                audio.ringtoneDevices.set(saved.ringer).catch(function() {});
            }
        }
    }

    // Mic level meter — bind the inputVolume listener once; only paint while
    // the panel is visible (panelVisible gate) so there's no idle work.
    function bindInputVolume() {
        if (!device || !device.audio || inputVolumeAudio === device.audio) return;
        inputVolumeAudio = device.audio;
        device.audio.on('inputVolume', function(v) {
            if (!panelVisible || !meterFillEl) return;
            // v is 0..1; clamp and render as a width %.
            var pct = Math.max(0, Math.min(1, v)) * 100;
            meterFillEl.style.width = pct.toFixed(0) + '%';
        });
    }

    // Hot-plug + permission-grant: repaint selects, re-apply persisted choices.
    function bindDeviceChange() {
        if (!device || !device.audio) return;
        device.audio.on('deviceChange', function() {
            log('audio deviceChange — repainting selects + re-asserting selection');
            applyOutputSupportUi();   // output support can flip when a device is (un)plugged
            populateDevices();
            restoreSavedSelections();
            paintDeviceRow();
            // Mid-call, the rep's saved mic may have come back (§ Mid-call mic recovery).
            if (activeCall) {
                micDeviceChanged = true;
                recordCallEvent('device-change', { mic: micLabel(), speaker: speakerLabel() });
            }
        });
    }

    // First entry to setup: two things are blocked until mic permission is
    // granted — device LABELS are blank (so the selects would show "Microphone /
    // Microphone 2" garbage), and the live mic METER can't run (Twilio only
    // polls inputVolume while it holds an input stream).
    //
    // `setInputDevice(id)` solves BOTH: it calls getUserMedia internally (the
    // one-time permission prompt), holds the stream (→ the meter starts), and
    // the SDK's deviceChange then fires with populated labels (→ repaint). So on
    // panel entry we acquire the saved-or-default input device. We do NOT do
    // this on page load — only here, on first setup entry. When there's no live
    // call, closePreview() releases the stream again (mic indicator off).
    //
    // If the rep already granted permission earlier (labels populated and we're
    // not mid-call holding a stream), we still acquire so the meter is live.
    //
    // Cross-browser: we grant the mic permission via a DIRECT
    // navigator.mediaDevices.getUserMedia() in the user-gesture path FIRST.
    // Safari is strict about the user gesture and rejects an indirect grant a
    // few layers down inside Twilio's setInputDevice. We immediately stop the
    // temp tracks (we only needed the grant + to unblock device labels), then
    // populate (labels are real now), then hand Twilio a guaranteed-valid id via
    // resolveInputId() so the SDK holds its own stream and the inputVolume meter
    // is live. Stopping the temp tracks does NOT kill the meter — the SDK
    // re-acquires in setInputDevice.
    function activateMicForPreview() {
        if (!device || !device.audio) return;

        function applyResolvedInput() {
            populateDevices();      // labels are real now (permission granted)
            var id = resolveInputId();
            if (!id) {
                warn('no microphone available — skipping setInputDevice');
                toast('No microphone found', 'error');
                return;
            }
            device.audio.setInputDevice(id)
            .then(function() { persistSelections(); })
            .catch(function(err) {
                warn('could not hold input stream for meter —', (err && err.message) || err);
                // Selects still render; the call can still proceed on the default mic.
                var mic = micError(err);
                if (mic) toast(mic.message, 'error');
            })
            .then(paintDeviceRow);  // a mid-call gear visit swaps the call's mic too
        }

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            // No getUserMedia (very old / insecure context) — fall back to letting
            // the SDK try directly. Still never throws into the call path.
            applyResolvedInput();
            return;
        }

        navigator.mediaDevices.getUserMedia({ audio: true })
        .then(function(stream) {
            // We only needed the grant + to unblock labels — release immediately.
            stream.getTracks().forEach(function(t) { t.stop(); });
            applyResolvedInput();
        })
        .catch(function(err) {
            warn('microphone getUserMedia failed —', err && err.name, (err && err.message) || err);
            var mic = micError(err);
            if (mic) toast(mic.message, 'error');
            populateDevices();      // selects still render; never throw into the call path
        });
    }

    // Apply the rep's saved (or default) input device to device.audio before
    // placing a call.  Returns a Promise that always resolves — failures are
    // logged and the call still proceeds on the browser default.  The failure is
    // kept in callMicError: if the SDK's own getUserMedia then fails too, its
    // 31402 error carries no cause, and this one says which (in use / no mic).
    // No-op when the desired device is already the active input (e.g. preview
    // path already set it), so calling this after the preview panel is harmless.
    function applyCallInputDevice() {
        if (!device || !device.audio) return Promise.resolve();
        // resolveInputId() returns an id guaranteed to exist in the available map
        // (saved → 'default' only on Chromium → first real id on Safari/FF), or
        // null when there's no mic at all. Never pass a non-existent id to the SDK.
        callMicError = null;
        var wantId = resolveInputId();
        if (!wantId) {
            warn('no microphone available — calling without an explicit input device');
            callMicError = { name: 'NotFoundError' };
            return Promise.resolve();
        }
        // No-op: already active — avoids redundant re-acquire.
        if (wantId === activeInputId()) {
            return Promise.resolve();
        }
        return device.audio.setInputDevice(wantId)
        .catch(function(err) {
            warn('applyCallInputDevice failed — calling on default mic —', (err && err.message) || err);
            callMicError = err;
        });
    }

    // ── Mid-call mic recovery ──
    //
    // Another device can take the mic mid-call — AirPods jump to the iPhone for a
    // notification sound, then come back. The browser ends or mutes the call's mic
    // track, and the SDK only re-acquires when the device leaves the device list
    // (and then lands on the default mic, not the rep's choice). So while a call is
    // answered we check the call's CURRENT track once a second — reading it fresh
    // each tick, so a swap by the SDK or the mic select is picked up without
    // rebinding anything. `muted` here is the SOURCE going silent; the Mute button
    // sets track.enabled and never trips it.
    //
    // Dead for MIC_DEAD_TICKS in a row → re-acquire, then back off (2s → 8s) and keep
    // retrying for the rest of the call, so the AirPods coming back seconds later
    // still recover. After a deviceChange the check also moves the call back to the
    // rep's saved mic when it reappears. Toasts fire once per lost spell.
    var MIC_DEAD_TICKS = 2;
    var MIC_BACKOFF_MIN_MS = 2000;
    var MIC_BACKOFF_MAX_MS = 8000;
    var MIC_HEALTHY_RESET_TICKS = 10;   // healthy this long before the backoff resets (flapping Bluetooth)
    var micTimer = null;
    var micDeadTicks = 0;
    var micHealthyTicks = 0;
    var micLost = false;
    var micRecovering = false;
    var micNextTryAt = 0;
    var micBackoffMs = MIC_BACKOFF_MIN_MS;
    var micDeviceChanged = false;

    function startMicCheck() {
        stopMicCheck();
        micBackoffMs = MIC_BACKOFF_MIN_MS;
        micTimer = setInterval(checkCallMic, 1000);
    }

    function stopMicCheck() {
        if (micTimer) { clearInterval(micTimer); micTimer = null; }
        micDeadTicks = 0;
        micHealthyTicks = 0;
        micLost = false;
        micRecovering = false;  // a switch or re-acquire that never settled can't block the next call
        micNextTryAt = 0;
        micDeviceChanged = false;
    }

    function callMicTrack() {
        var stream = activeCall && activeCall.getLocalStream && activeCall.getLocalStream();
        return (stream && stream.getAudioTracks()[0]) || null;
    }

    function checkCallMic() {
        if (!activeCall || micRecovering || !device || !device.audio) return;
        var track = callMicTrack();
        var dead = !track || track.readyState === 'ended' || track.muted;
        if (!dead) {
            micDeadTicks = 0;
            if (++micHealthyTicks >= MIC_HEALTHY_RESET_TICKS) micBackoffMs = MIC_BACKOFF_MIN_MS;
            if (micLost) {
                micLost = false;
                toast('Microphone reconnected', 'success');
            }
            // The rep's saved mic came back (the AirPods returned) — move the call onto it.
            if (micDeviceChanged) {
                micDeviceChanged = false;
                var want = resolveInputId();
                if (want && want !== activeInputId()) reacquireCallMic(want, 'saved mic is back');
            }
            return;
        }
        micHealthyTicks = 0;
        if (++micDeadTicks < MIC_DEAD_TICKS || Date.now() < micNextTryAt) return;
        if (!micLost) {
            micLost = true;
            toast('Microphone lost — reconnecting…', 'warning');
        }
        var id = resolveInputId();
        if (!id) return;   // no mic at all; keep checking in case one appears
        reacquireCallMic(id, track ? 'mic track ' + (track.readyState === 'ended' ? 'ended' : 'muted') : 'no mic track');
        micNextTryAt = Date.now() + micBackoffMs;
        micBackoffMs = Math.min(micBackoffMs * 2, MIC_BACKOFF_MAX_MS);
    }

    // Take a fresh mic stream and swap it onto the live call. setInputDevice() is a
    // no-op when the id is already the active input, which is exactly the AirPods
    // case, so the same id goes through setAudioConstraints(), whose documented
    // effect is a forced re-acquire of the current input.
    // Resolves to true when the new stream is on the call, false when it failed.
    function reacquireCallMic(id, reason) {
        var audio = device.audio;
        warn(reason, '— re-acquiring mic', id);
        micRecovering = true;
        micDeadTicks = 0;
        var p = (audio.inputDevice && audio.inputDevice.deviceId === id)
            ? audio.setAudioConstraints(audio.audioConstraints || {})
            : audio.setInputDevice(id);
        return p.then(function() { return true; }, function(err) {
            // The next dead tick retries after the backoff; the toast already said "lost".
            warn('mic re-acquire failed —', (err && err.name), (err && err.message) || err);
            return false;
        }).then(function(ok) {
            micRecovering = false;
            paintDeviceRow();
            recordCallEvent('reacquire', { reason: reason, ok: ok, mic: micLabel() });
            return ok;
        });
    }

    // ── In-call device row + silent-mic check (Loop #1427) ──
    //
    // The pre-call panel can pass while the call still sends silence: the track is
    // live, so checkCallMic() sees nothing wrong, and the callee hears nothing. The
    // Call `volume` event measures the stream the PeerConnection actually SENDS
    // (about every 50ms, 0..1 on a -100..-30 dB scale, following a mid-call
    // setInputDevice), so it drives both the in-call mic bar and this check.
    //
    // On an answered, unmuted call, input under MIC_SILENT_LEVEL for MIC_SILENT_MS
    // starts a silent spell (startMicSpell); so does a Twilio
    // `constant-audio-input-level` warning. A spell shows the mic banner and posts
    // stage=mic to client-error; it does not re-acquire the mic, because on dead-mic
    // prod calls a re-acquire never brought audio back and switching devices did (the
    // banner's "Switch mic" opens the mic picker's menu). It ends
    // (endMicSpell) only when input is back above the threshold AND no Twilio
    // warning is open, when the rep mutes, or at hang-up. The network banner wins
    // over the mic banner.
    // sampleCallInput() logs the input once a second so the threshold can be tuned.

    // On prod calls a dead mic reads exactly 0.000 for the whole call (1290, 1292, 1300,
    // 1305), while a live mic under noise suppression dips to 0.000 for up to ~5 s in a
    // quiet moment (1297 raised 3 false spells at 0.01 / 5 s). So the level only has to
    // sit a few analyser steps above zero (the smallest nonzero reading is about
    // 0.00025), and the window has to outlast those dips.
    var MIC_SILENT_LEVEL = 0.001;
    var MIC_SILENT_MS = 8000;
    var MIC_LABEL_MAX = 90;              // per label in a report; the server keeps 300 chars of message

    var micBannerEl = null;              // the "mic isn't coming through" banner
    var micNameEl = null;                // in-call device row: mic picker label
    var speakerNameEl = null;            // in-call device row: speaker picker label
    var micPickerEl = null;              // in-call device row: mic picker (dropdown trigger)
    var micMenuEl = null;                // in-call device row: mic picker's device menu
    var speakerDropdownEl = null;        // in-call device row: speaker picker dropdown
    var speakerMenuEl = null;            // in-call device row: speaker picker's device menu
    var speakerTextEl = null;            // in-call device row: plain speaker text (no output selection)
    var callMeterFillEl = null;          // in-call device row: mic bar fill

    var audioWatchOn = false;            // answered — the silence check and the sampler run
    var micSampleTimer = null;
    var micSecond = null;                // volume stats for the current second (sampleCallInput)
    var quietWindow = null;              // stats since input went under the threshold, or null
    var lastInputVolume = null;
    var twilioMicWarning = false;        // a constant-audio-input-level warning is open
    var micSpell = null;                 // {code, startedAt, min, sum, n} while a spell is open

    function newLevelStats(now) {
        return { since: now || Date.now(), min: null, max: null, sum: 0, n: 0 };
    }

    function avgLevel(stats) {
        return stats && stats.n ? stats.sum / stats.n : null;
    }

    function addLevel(stats, v) {
        stats.min = stats.min === null ? v : Math.min(stats.min, v);
        stats.max = stats.max === null ? v : Math.max(stats.max, v);
        stats.sum += v;
        stats.n += 1;
    }

    function fmtLevel(v) {
        return (v === null || v === undefined) ? 'n/a' : v.toFixed(3);
    }

    // What the call is actually sending from: the live track's label first (it is the
    // real device even when applyCallInputDevice fell back to the browser default),
    // then the selected input.
    function micLabel() {
        var track = callMicTrack();
        if (track && track.label) return track.label;
        if (!device || !device.audio) return '';
        var inDev = device.audio.inputDevice;
        if (inDev && inDev.label) return inDev.label;
        var info = device.audio.availableInputDevices.get(activeInputId());
        return (info && info.label) || 'Default microphone';
    }

    function speakerLabel() {
        if (!device || !device.audio) return '';
        if (!outputSelectionSupported()) return 'System default speaker';
        var info = device.audio.availableOutputDevices.get(firstOutputId(device.audio.speakerDevices));
        return (info && info.label) || 'Default speaker';
    }

    // Rebuild a picker's device menu from a Map<deviceId, MediaDeviceInfo>, with a
    // check on the current device. Labels go in as text, never HTML.
    function fillDeviceMenu(menuEl, devicesMap, activeId, fallbackLabel) {
        if (!menuEl) return;
        menuEl.innerHTML = '';
        devicesMap.forEach(function(info, id) {
            var item = document.createElement('button');
            item.type = 'button';
            item.className = 'dropdown-item';
            item.setAttribute('data-device-id', id);
            if (id === activeId) {
                item.setAttribute('aria-current', 'true');
                var check = document.createElement('i');
                check.className = 'fa-solid fa-check';
                check.setAttribute('aria-hidden', 'true');
                item.appendChild(check);
                item.appendChild(document.createTextNode(' '));
            }
            item.appendChild(document.createTextNode(info.label || fallbackLabel));
            menuEl.appendChild(item);
        });
    }

    // The mic to check in the picker's menu. Where the browser lists no 'default'
    // entry (Safari, Firefox) and no input has been set, match the live track's
    // label instead.
    function menuInputId(audio) {
        var id = activeInputId();
        if (audio.availableInputDevices.has(id)) return id;
        var label = micLabel();
        var match = null;
        audio.availableInputDevices.forEach(function(info, key) {
            if (!match && info.label && info.label === label) match = key;
        });
        return match || id;
    }

    // Close a device picker left open when the call ends, so its menu (a sheet
    // on a phone) doesn't outlive the call. A trigger click toggles it shut.
    function closeDevicePickers() {
        [micPickerEl, speakerDropdownEl && speakerDropdownEl.querySelector('[data-dropdown]')].forEach(function(trigger) {
            var dropdown = trigger && trigger.closest('.dropdown');
            if (dropdown && dropdown.classList.contains('show')) trigger.click();
        });
    }

    // Refresh the in-call mic and speaker pickers and their menus (deviceChange, a
    // mic or speaker switch, a re-acquire, connect, answer).
    function paintDeviceRow() {
        if (micNameEl) micNameEl.textContent = micLabel() || 'Microphone';
        if (speakerNameEl) speakerNameEl.textContent = speakerLabel() || 'Speaker';
        if (!device || !device.audio) return;
        var audio = device.audio;
        var outputs = outputSelectionSupported();
        fillDeviceMenu(micMenuEl, audio.availableInputDevices, menuInputId(audio), 'Default microphone');
        if (outputs) {
            fillDeviceMenu(speakerMenuEl, audio.availableOutputDevices,
                           firstOutputId(audio.speakerDevices), 'Default speaker');
        }
        if (speakerDropdownEl) speakerDropdownEl.hidden = !outputs;
        if (speakerTextEl) speakerTextEl.hidden = outputs;
    }

    function paintCallMeter(v) {
        if (!callMeterFillEl) return;
        callMeterFillEl.style.width = (Math.max(0, Math.min(1, v || 0)) * 100).toFixed(0) + '%';
    }

    // call.on('volume') — every ~50ms from connect. The bar always paints; the
    // silence check and the per-second sample run only once the call is answered.
    function onCallVolume(inputVolume) {
        paintCallMeter(inputVolume);
        if (!audioWatchOn || !activeCall) return;
        lastInputVolume = inputVolume;
        if (micSecond) addLevel(micSecond, inputVolume);
        checkMicSilence(inputVolume);
    }

    function checkMicSilence(v) {
        // Mute sends silence on purpose — never count it.
        if (activeCall.isMuted && activeCall.isMuted()) { quietWindow = null; return; }
        if (micSpell) addLevel(micSpell, v);
        if (v < MIC_SILENT_LEVEL) {
            if (!quietWindow) quietWindow = newLevelStats();
            addLevel(quietWindow, v);
            if (!micSpell && Date.now() - quietWindow.since >= MIC_SILENT_MS) {
                startMicSpell('silent', quietWindow);
            }
            return;
        }
        quietWindow = null;
        if (micSpell && !twilioMicWarning) endMicSpell('mic-back');
    }

    // Once a second after answer: the average input over the last second, logged
    // so the threshold can be tuned from a real call.
    function sampleCallInput() {
        var s = micSecond;
        micSecond = newLevelStats();
        if (!s || !activeCall) return;
        var level = avgLevel(s);
        var muted = activeCall.isMuted && activeCall.isMuted();
        recordInputLevel(level);
        log('mic input', fmtLevel(level),
            s.n ? '(min ' + fmtLevel(s.min) + ', max ' + fmtLevel(s.max) + ', ' + s.n + ' samples)' : '(no volume events)',
            muted ? '— muted' : '');
    }

    function micReportMessage(stats, extra) {
        var avg = avgLevel(stats);
        var clip = function(s) { return String(s || '').slice(0, MIC_LABEL_MAX); };
        return 'min=' + fmtLevel(stats && stats.min) + ' avg=' + fmtLevel(avg) + (extra ? ' ' + extra : '') +
               ' mic="' + clip(micLabel()) + '" speaker="' + clip(speakerLabel()) + '"';
    }

    // A silent spell begins. `code` is 'silent' (our check) or 'twilio-constant'.
    // `win` holds the input stats that tripped it.
    function startMicSpell(code, win) {
        var stats = win || micSecond || newLevelStats();
        var spell = {
            code: code, startedAt: Date.now(),
            min: stats.min, max: stats.max, sum: stats.sum, n: stats.n,
        };
        micSpell = spell;
        warn('silent mic spell started —', code, 'min', fmtLevel(stats.min),
             'avg', fmtLevel(avgLevel(stats)));
        syncMicBanner();
        reportError('mic', { code: code, message: micReportMessage(stats, '') }, activeAttemptId);
        recordCallEvent('spell-start', { code: code, min: round3(stats.min), avg: round3(avgLevel(stats)) });
    }

    // The spell ends: 'mic-back' (input above the threshold, no Twilio warning open)
    // 'silent-end' (the call ended while still silent), or 'muted' (the rep muted).
    function endMicSpell(code) {
        var spell = micSpell;
        if (!spell) return;
        micSpell = null;
        syncMicBanner();
        var ms = Date.now() - spell.startedAt;
        log('silent mic spell ended —', code, 'after', ms, 'ms');
        recordCallEvent('spell-end', { code: code, back: code === 'mic-back', ms: ms, min: round3(spell.min),
                                      avg: round3(avgLevel(spell)) });
        reportError('mic', {
            code: code,
            message: micReportMessage(spell, 'start=' + spell.code + ' ms=' + ms),
        }, activeAttemptId);
    }

    // The network banner wins: the mic banner hides under it and comes back when
    // it clears, if the spell is still open.
    function syncMicBanner() {
        if (!micBannerEl) return;
        var netUp = !!(netBannerEl && !netBannerEl.hidden);
        micBannerEl.hidden = !micSpell || netUp;
    }

    // Twilio's own one-way-audio signal. The SDK drops it, and its clear, while the
    // call is muted — so toggleMute() drops an open one too (see onMuteChanged).
    function onCallWarning(name) {
        log('call warning:', name);
        recordCallEvent('warning', { name: name });
        if (name !== 'constant-audio-input-level' || twilioMicWarning) return;
        // A warning raised while it rings does not count: a rep who stays quiet through
        // the ringing is not a dead mic, and our own check catches a dead one in MIC_SILENT_MS.
        if (!audioWatchOn) return;
        twilioMicWarning = true;
        if (!micSpell) startMicSpell('twilio-constant', quietWindow);
    }

    function onCallWarningCleared(name) {
        log('call warning cleared:', name);
        recordCallEvent('warning-cleared', { name: name });
        if (name !== 'constant-audio-input-level') return;
        twilioMicWarning = false;
        if (micSpell && !quietWindow && lastInputVolume !== null && lastInputVolume >= MIC_SILENT_LEVEL) {
            endMicSpell('mic-back');
        }
    }

    // Mute or unmute. Mute sends silence on purpose, so it closes an open spell as
    // 'muted' (never read as a mic failure). The SDK swallows the warning's clear while
    // muted, so the open warning goes too; after unmute the check starts fresh.
    function onMuteChanged() {
        var muted = !!(activeCall && activeCall.isMuted && activeCall.isMuted());
        recordCallEvent(muted ? 'mute' : 'unmute');
        quietWindow = null;
        twilioMicWarning = false;
        if (muted) endMicSpell('muted');
    }

    // Answer: start the silence check and the once-a-second sample.
    function startCallAudioWatch() {
        if (micSampleTimer) clearInterval(micSampleTimer);
        audioWatchOn = true;
        micSecond = newLevelStats();
        quietWindow = null;
        micSampleTimer = setInterval(sampleCallInput, 1000);
        paintDeviceRow();
    }

    // Call over (wrap-up, a failed dial, a reset): close an open spell as
    // 'silent-end', stop the timers, hide the banner, flatten the bar.
    function stopCallAudioWatch() {
        if (micSpell) endMicSpell('silent-end');
        if (micSampleTimer) { clearInterval(micSampleTimer); micSampleTimer = null; }
        audioWatchOn = false;
        micSecond = null;
        quietWindow = null;
        lastInputVolume = null;
        twilioMicWarning = false;
        syncMicBanner();
        paintCallMeter(0);
    }

    // ── Call audio diagnostics (Loop #1427) ──
    //
    // Per call, from Dial: `events` and `levels`, posted once to calls/<id>/diagnostics/
    // when the call ends (enterWrapup, or resetCallState for a call that never reached
    // wrap-up), and by sendBeacon if the tab closes mid-call. A repeat lands as an
    // overwrite server-side. Nothing in the UI reads them; they are for queries.
    //
    // Event times: `t` is ms since answer; before answer it is ms since Dial, with
    // `pre: true`. Each level is one second's average Call `volume` input from answer,
    // 3 decimals; -1 marks a second with no volume events, so the list stays one per second.
    // The keepalive fetch and the beacon both stop at 64 KB. Sent as multipart (no
    // percent-encoding), 300 events plus 1800 levels fit unless the events carry many
    // device labels; sendDiagnostics drops keepalive past DIAG_KEEPALIVE_BYTES (UTF-8,
    // leaving room for the multipart framing and the CSRF field).
    var DIAG_EVENTS_MAX = 300;
    var DIAG_KEEPALIVE_BYTES = 60000;
    var DIAG_LEVELS_MAX = 1800;          // 30 minutes
    var diag = null;                     // {dialAt, answerAt, events, levels, sent} for the current call

    function startDiagnostics() {
        diag = { dialAt: Date.now(), answerAt: null, events: [], levels: [], sent: false };
    }

    function recordCallEvent(name, details) {
        if (!diag || diag.sent || diag.events.length >= DIAG_EVENTS_MAX) return;
        var now = Date.now();
        var ev = { t: now - (diag.answerAt || diag.dialAt), e: name };
        if (!diag.answerAt) ev.pre = true;
        Object.assign(ev, details || {});
        diag.events.push(ev);
    }

    function round3(v) {
        return (v === null || v === undefined) ? null : Math.round(v * 1000) / 1000;
    }

    function recordInputLevel(level) {
        if (!diag || diag.sent || diag.levels.length >= DIAG_LEVELS_MAX) return;
        diag.levels.push(level === null ? -1 : round3(level));
    }

    // What the call is about to send with, for calls/start's client_info. The constraints
    // come from the held mic track (the panel acquired it), else what the dock requested.
    function callClientInfo() {
        var audio = device && device.audio;
        var stream = audio && audio.inputStream;
        var track = stream && stream.getAudioTracks()[0];
        var settings = (track && track.getSettings) ? track.getSettings() : null;
        var src = settings || (audio && audio.audioConstraints) || {};
        return {
            ua: navigator.userAgent,
            sdk: Device.version,
            constraints: {
                echoCancellation: src.echoCancellation,
                noiseSuppression: src.noiseSuppression,
                autoGainControl: src.autoGainControl,
            },
            constraints_from: settings ? 'track' : 'requested',
            output_selection: !!(audio && audio.isOutputSelectionSupported),
        };
    }

    function diagnosticsBody() {
        var body = new FormData();
        body.set('csrfmiddlewaretoken', getCsrfToken() || '');
        body.set('client_events', JSON.stringify(diag.events));
        body.set('input_levels', JSON.stringify(diag.levels));
        return body;
    }

    function diagnosticsBytes(body) {
        return new Blob([body.get('client_events'), body.get('input_levels')]).size;
    }

    function diagnosticsUrl() {
        return '/telephony/calls/' + activeAttemptId + '/diagnostics/';
    }

    // The end-of-call send — once per call.
    function sendDiagnostics() {
        if (!diag || diag.sent || !activeAttemptId) return;
        var url = diagnosticsUrl();
        var body = diagnosticsBody();
        diag.sent = true;
        // keepalive: a rep who hangs up and closes the tab at once still lands it. Past
        // the 64 KB limit keepalive would reject, so a large body goes as a plain fetch.
        var keepalive = diagnosticsBytes(body) < DIAG_KEEPALIVE_BYTES;
        fetch(url, { method: 'POST', credentials: 'same-origin', body: body, keepalive: keepalive })
        .then(function(r) { if (!r.ok) warn('diagnostics POST HTTP', r.status); })
        .catch(function(err) { warn('diagnostics POST failed —', err && err.message); });
    }

    // The tab is closing mid-call, or while a failed dial waits to retry (activeCall is
    // null then, but the attempt exists): a beacon survives that. The Device's own
    // pagehide listener destroys it and disconnects the call, whose wrap-up would run the
    // fetch above; init() registers this one in the capture phase so it runs first, and a
    // queued beacon counts as the send.
    function beaconDiagnostics() {
        if (!diag || diag.sent || !activeAttemptId || !navigator.sendBeacon) return;
        recordCallEvent('pagehide');
        if (navigator.sendBeacon(diagnosticsUrl(), diagnosticsBody())) diag.sent = true;
    }

    // Open the device panel. mode: 'dial' (a call is pending — Dial places it)
    // or 'settings' (gear, no pending call — Dial acts as Save/Close).
    function openPreview(mode) {
        previewMode = mode;
        panelVisible = true;
        if (dock) {
            dock.classList.remove('is-idle', 'is-active', 'is-wrapup');
            dock.classList.add('is-preview');
        }
        clearKeypad();  // also syncs the keypad toggle button's aria/visual state (not just the class)
        setContext(mode === 'dial' && pendingCall ? (pendingCall.label || 'Dialer') : 'Audio devices');
        // From select: only for a pending acqs call with 2+ lines (loadCallLines
        // filled it).  The gear never shows it — the line can't change mid-call.
        if (lineFieldEl) lineFieldEl.hidden = !(mode === 'dial' && callLines.length > 1);

        // Device not ready (token fetch failed / not registered yet): show the
        // not-ready note, disable the selects, but never throw.
        var ready = !!(device && device.audio);
        if (previewUnavailableEl) previewUnavailableEl.hidden = ready;
        [micSelect, speakerSelect, ringerSelect, speakerTestBtn].forEach(function(el) {
            if (el) el.disabled = !ready;
        });
        // Relabel the footer for the standalone settings case.
        if (previewDialBtn) {
            previewDialBtn.disabled = !ready && mode === 'dial';
            previewDialBtn.innerHTML = (mode === 'dial')
                ? '<i class="fa-solid fa-phone"></i> Dial'
                : '<i class="fa-solid fa-check"></i> Save';
        }
        if (previewCancelBtn) previewCancelBtn.textContent = (mode === 'dial') ? 'Cancel' : 'Close';

        if (!ready) {
            setStatus(device ? 'Registering…' : 'Dialer not ready');
            return;
        }

        setStatus(mode === 'dial' ? 'Check your audio, then Dial' : 'Audio devices');
        applyOutputSupportUi();
        bindInputVolume();
        populateDevices();          // paint whatever we have now (may be blank labels)
        restoreSavedSelections();   // re-assert output choices (mic handled below)
        activateMicForPreview();    // permission + live stream → labels + meter
        // Dial has focus, so Enter places the call.
        if (mode === 'dial' && previewDialBtn) previewDialBtn.focus();
    }

    // Close the preview panel. Pass keepInput=true when we're closing in order
    // to DIAL — the call about to be placed needs the selected input stream, so
    // we must not release it. Otherwise (Cancel / settings-close / idle) release
    // the mic stream so the browser's mic-in-use indicator turns off.
    function closePreview(keepInput) {
        panelVisible = false;
        if (meterFillEl) meterFillEl.style.width = '0%';
        // During a live call the stream belongs to the call — leave it alone.
        if (!keepInput && !activeCall && device && device.audio && device.audio.inputDevice) {
            device.audio.unsetInputDevice().catch(function(err) {
                warn('unsetInputDevice failed —', (err && err.message) || err);
            });
        }
    }

    // ── Mute ──

    function setMuteUi(muted) {
        if (!muteBtn) return;
        muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
        muteBtn.classList.toggle('is-on', muted);
        muteBtn.title = muted ? 'Unmute' : 'Mute';
        muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
        var icon = muteBtn.querySelector('i');
        if (icon) icon.className = muted ? 'fa-solid fa-microphone-slash' : 'fa-solid fa-microphone';
    }

    function toggleMute() {
        if (!activeCall) return;
        var muted = activeCall.isMuted ? activeCall.isMuted() : false;
        activeCall.mute(!muted);
        setMuteUi(!muted);
        onMuteChanged();
        log('mute', !muted);
    }

    // ── DTMF keypad ──

    // Keypad mode is an EXCLUSIVE view inside the expand surface: when on, the
    // keypad shows and the notes/outcome/next-action/footer hide (driven purely
    // by the `is-keypad` class on the dock). The header stays visible in both.
    function toggleKeypad() {
        if (!dock || !keypadToggleBtn) return;
        var open = dock.classList.toggle('is-keypad');
        keypadToggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        keypadToggleBtn.classList.toggle('is-on', open);
    }

    // Force keypad mode off + sync the toggle button (used on wrap-up / reset so
    // the next call never starts stuck in keypad view).
    function clearKeypad() {
        if (dock) dock.classList.remove('is-keypad');
        if (keypadToggleBtn) {
            keypadToggleBtn.setAttribute('aria-expanded', 'false');
            keypadToggleBtn.classList.remove('is-on');
        }
    }

    function sendDigit(d) {
        if (!activeCall || !d) return;
        activeCall.sendDigits(d);
        log('DTMF', d);
    }

    // ── Outcome chips (single-select) ──

    function selectOutcome(value) {
        selectedOutcome = value;
        if (outcomesEl) {
            outcomesEl.querySelectorAll('[data-outcome]').forEach(function(chip) {
                chip.classList.toggle('chip-active', chip.getAttribute('data-outcome') === value);
            });
        }
        if (value !== 'bad_number') selectPhoneStatus(null);
        if (badNumberWrap) badNumberWrap.hidden = value !== 'bad_number';
        syncNextStep();
        syncMakeMain();
        syncWrapupActions();
        log('outcome selected:', value);
    }

    // Replace the chips with this call's outcome set ([{value, label}] from
    // calls/start). textContent keeps the labels inert. Pass [] to clear.
    function renderOutcomes(outcomes) {
        if (!outcomesEl) return;
        var chips = (outcomes || []).map(function(outcome) {
            var chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'chip chip-sm chip-muted';
            chip.setAttribute('data-outcome', outcome.value);
            chip.textContent = outcome.label;
            return chip;
        });
        outcomesEl.replaceChildren.apply(outcomesEl, chips);
    }

    function selectPhoneStatus(status) {
        selectedPhoneStatus = status;
        if (!badNumberWrap) return;
        badNumberWrap.querySelectorAll('[data-phone-status]').forEach(function(chip) {
            chip.classList.toggle('chip-active', chip.getAttribute('data-phone-status') === status);
        });
    }

    // A call result, and a next step on a lead's call, are required to leave wrap-up, unless the
    // call errored out with nothing to describe.
    function wrapupComplete() {
        if (!selectedOutcome) return false;
        if (selectedOutcome === 'bad_number') return !!selectedPhoneStatus;
        if (!nextStepShown()) return true;
        if (nextStep === 'follow_up') return !!(nextActionDays || (nextDateEl && nextDateEl.value));
        if (nextStep === 'hand_off') return !!(handOffPersonEl && handOffPersonEl.value);
        return !!nextStep;
    }

    // An errored call may save blank. Once a result is picked, it needs what any call needs.
    function wrapupSavable() {
        return wrapupComplete() || (dialFailed && !selectedOutcome);
    }

    function syncWrapupActions() {
        if (saveBtn) saveBtn.disabled = !wrapupSavable();
        if (dismissBtn) dismissBtn.hidden = !dialFailed;
    }

    // Show "Make this the main number" only while the selected outcome is one the
    // server listed for this call. Leasing/tenant calls get an empty list.
    function syncMakeMain() {
        if (!makeMainWrap) return;
        makeMainWrap.hidden = makeMainOutcomes.indexOf(selectedOutcome) === -1;
    }

    // Click-delegation on the chip container, so chips rendered per call need no binding.
    function bindOutcomeChips(el) {
        if (!el) return;
        el.addEventListener('click', function(e) {
            var chip = e.target.closest('[data-outcome]');
            if (!chip) return;
            e.preventDefault();
            var val = chip.getAttribute('data-outcome');
            // Toggle off if the already-selected chip is clicked again.
            selectOutcome(selectedOutcome === val ? null : val);
        });
    }

    // ── Next-action quick-date chips ──

    function selectNextDays(days) {
        nextActionDays = days;
        if (days && nextDateEl) nextDateEl.value = '';
        if (nextActionWrap) {
            nextActionWrap.querySelectorAll('[data-next-days]').forEach(function(chip) {
                chip.classList.toggle('chip-active', chip.getAttribute('data-next-days') === String(days));
            });
        }
        syncWrapupActions();
    }

    // Follow up starts on Tomorrow, so the common case is one tap.
    function selectNextStep(step) {
        nextStep = step;
        if (nextActionWrap) {
            nextActionWrap.querySelectorAll('[data-next-step]').forEach(function(chip) {
                chip.classList.toggle('chip-active', chip.getAttribute('data-next-step') === step);
            });
        }
        if (step === 'follow_up' && !nextActionDays && !(nextDateEl && nextDateEl.value)) selectNextDays('1');
        syncNextStep();
        syncWrapupActions();
    }

    // Next step shows once calls/<pk>/next-step/ says the rep may write the lead's next action.
    // Bad number, a call with no lead, and a failed load have no next step.
    function nextStepShown() {
        return !!(activeLeadPk && nextStepInfo && nextStepInfo.canEdit && selectedOutcome !== 'bad_number');
    }

    // Which Next step fields show.
    function syncNextStep() {
        if (!nextActionWrap) return;
        nextActionWrap.hidden = !nextStepShown();
        if (followUpWrap) followUpWrap.hidden = nextStep !== 'follow_up';
        if (handOffWrap) handOffWrap.hidden = nextStep !== 'hand_off';
        if (nextTextWrap) nextTextWrap.hidden = nextStep !== 'follow_up' && nextStep !== 'hand_off';
    }

    // Fill the row from this call's calls/<pk>/next-step/. Pass null to clear.
    function renderNextStep(info) {
        nextStepInfo = info || null;
        if (!nextActionWrap) return;
        var bookChip = nextActionWrap.querySelector('[data-next-step="book"]');
        var handOffChip = nextActionWrap.querySelector('[data-next-step="hand_off"]');
        var people = nextStepInfo ? nextStepInfo.people : [];
        if (bookChip) bookChip.hidden = !(nextStepInfo && nextStepInfo.bookUrl);
        if (handOffChip) handOffChip.hidden = !people.length;
        if (!handOffPersonEl) return;
        var blank = document.createElement('option');
        blank.value = '';
        blank.textContent = 'Choose a person';
        var options = people.map(function(person) {
            var option = document.createElement('option');
            option.value = person.id;
            option.textContent = person.name;
            return option;
        });
        handOffPersonEl.replaceChildren.apply(handOffPersonEl, [blank].concat(options));
        syncNextStep();
        syncWrapupActions();
    }

    function isoDate(date) {
        return date.getFullYear() + '-' +
               String(date.getMonth() + 1).padStart(2, '0') + '-' +
               String(date.getDate()).padStart(2, '0');
    }

    // The fields one Save writes to the lead's next action, or null when the step writes none.
    // "Call back" fills in only on a lead with no text, so a rep's existing text stays.
    function nextActionValues() {
        if (!nextStepShown() || (nextStep !== 'follow_up' && nextStep !== 'hand_off')) return null;
        var values = {};
        var text = nextActionTextEl ? nextActionTextEl.value.trim() : '';
        if (text) values.next_action = text;
        else if (nextStepInfo && !nextStepInfo.hasNextAction) values.next_action = 'Call back';
        if (nextStep === 'hand_off') {
            values.next_action_date = isoDate(new Date());
            values.next_action_assignee = handOffPersonEl.value;
        } else if (nextDateEl && nextDateEl.value) {
            values.next_action_date = nextDateEl.value;
        } else {
            var date = new Date();
            date.setDate(date.getDate() + parseInt(nextActionDays, 10));
            values.next_action_date = isoDate(date);
        }
        return values;
    }

    function getCsrfToken() {
        if (window._cc && window._cc.getCsrfToken) return window._cc.getCsrfToken();
        // Fallback: read cookie directly (project uses csrftoken_houses, then csrftoken)
        var m = document.cookie.match(/(?:^|; )csrftoken_houses=([^;]*)/);
        if (m) return decodeURIComponent(m[1]);
        m = document.cookie.match(/(?:^|; )csrftoken=([^;]*)/);
        return m ? decodeURIComponent(m[1]) : null;
    }

    // Shared same-origin form POST with CSRF. Returns the fetch promise.
    function postForm(url, body) {
        var headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        var csrf = getCsrfToken();
        if (csrf) headers['X-CSRFToken'] = csrf;
        else warn('no CSRF token found — POST', url, 'will likely 403');
        return fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: headers,
            body: body.toString(),
        });
    }

    // ── Token fetch helper ──

    function fetchToken(url) {
        return fetch(url, { credentials: 'same-origin' })
        .then(function(r) {
            if (!r.ok) {
                var e = new Error('token HTTP ' + r.status);
                e.status = r.status;
                throw e;
            }
            return r.json();
        });
    }

    // ── Error reporting ──

    // Send a Twilio browser error to the server log (otherwise it only reaches the rep's console).
    // With an attempt id the server answers whether that attempt ever reached Twilio; `final`
    // means the dock gave up — the server marks a never-placed attempt failed and pages on-call.
    // Resolves to the JSON body, or {} if the report itself failed.
    function reportError(stage, err, attemptId, final) {
        var body = new URLSearchParams();
        body.set('stage', stage);
        body.set('code', String((err && err.code) || ''));
        body.set('message', String((err && err.message) || err || '').slice(0, 300));
        if (attemptId) body.set('attempt_id', attemptId);
        if (final) body.set('final', '1');
        return postForm(REPORT_URL, body)
        .then(function(r) { return r.ok ? r.json() : {}; })
        .catch(function() { return {}; });
    }

    // ── Device setup + self-healing ──
    //
    // A Device can die quietly while the page sits open: the SDK renews the token from a timer
    // that a sleeping laptop or a throttled background tab never fires, and a dropped websocket
    // may never report itself. Nothing short of a new Device recovers either, so the dock
    // rebuilds one (fresh token, fresh websocket) whenever the Device errors while idle, on
    // backoff, and re-checks when the tab comes back into view or the network returns.

    function clearSetupTimer() {
        if (setupTimer) { clearTimeout(setupTimer); setupTimer = null; }
    }

    function setupDevice() {
        if (settingUp) return;
        settingUp = true;
        var gen = ++setupGen;
        // A token fetch or register() on a dead connection can hang with no event at all; without
        // this the settingUp latch would block every later rebuild until the page reloads.
        clearSetupTimer();
        setupTimer = window.setTimeout(function() {
            setupTimer = null;
            if (!settingUp) return;
            warn('Device setup did not finish in', SETUP_TIMEOUT_MS / 1000, 's — retrying');
            setupGen++;  // if the hung fetch lands later, it must not create a second Device
            settingUp = false;
            setIdleStatus('Reconnecting…');
            scheduleRebuild();
        }, SETUP_TIMEOUT_MS);
        log('fetching Twilio token from', tokenUrl);
        setIdleStatus('Connecting…');  // registering affordance — see resetIdle
        fetchToken(tokenUrl)
        .then(function(data) {
            if (gen !== setupGen) { log('ignoring a token from an abandoned setup'); return; }
            if (!data || !data.token) throw new Error('no token in response');
            tokenFetchedAt = Date.now();
            log('token OK (identity', (data.identity || '?') + ') — creating Twilio Device');
            setIdleStatus('Registering…');
            // Precise signaling errors keep busy / unavailable / declined apart from a
            // real 31005 network drop — see FAR_END_ERRORS.
            device = new Device(data.token, { logLevel: 'error', enableImprovedSignalingErrorPrecision: true });

            device.on('registered', function() {
                settingUp = false;
                clearSetupTimer();
                deviceReady = true;
                rebuildFailures = 0;
                log('Device registered — ready to call');
                // Our own ringback replaces it — see startRingback.
                device.audio.outgoing(false);
                // Wire audio-device handling now that device.audio exists.
                // NOTE: we do NOT request mic permission here (that would prompt
                // on page load) — populate happens lazily on first setup entry.
                bindDeviceChange();
                bindInputVolume();
                // Re-apply any previously-saved device choices (labels may be
                // blank until permission is granted, but ids still resolve).
                restoreSavedSelections();
                // NOTE: do NOT clear a reconnecting banner here on re-registration.
                // The signaling websocket can flap back up while the call is
                // already dying — Device `registered` is NOT an authoritative
                // "call recovered" signal. Only the Call's own `reconnected`
                // event clears the banner (clearReconnecting); otherwise the
                // `disconnect`/`error` path flips it to "dropped — network".
                setIdleStatus('Ready');
                runQueuedDial();
            });
            device.on('unregistered', function() {
                deviceReady = false;
                if (activeCall || dialInFlight) return;
                log('Device unregistered while idle — scheduling a rebuild');
                setIdleStatus('Reconnecting…');
                scheduleRebuild();
            });
            device.on('error', function(err) {
                var code = err && err.code;
                warn('Device error:', code, (err && err.message) || err, err);
                // A mid-call signaling drop (31005 / websocket 1006) surfaces here
                // too, but the Call's own reconnecting/error/disconnect events drive
                // the dock banner — the device handler stays out of the call UI so a
                // signaling flap that the call survives can't leave a stuck banner.
                // Once connect() has a Call, its own events own the outcome — a signaling blip
                // the SDK recovers from must not hang up a call that may be about to ring.
                if (activeCall) return;
                deviceReady = false;
                settingUp = false;
                clearSetupTimer();
                // connect() still pending: the dial path owns the failure (retry once, then give up).
                if (dialInFlight) { failDial(err); return; }
                lastDeviceError = err;
                if (!rebuildTimer) reportError('device', err);  // one report per burst, not per error
                // Don't stomp the status during wrap-up either — the trailing
                // 31005s after a network drop would otherwise overwrite the
                // "Call dropped — network" wrap-up.
                setIdleStatus('Reconnecting…');
                scheduleRebuild();
            });
            device.on('tokenWillExpire', function() {
                log('token expiring — refreshing');
                refreshToken();
            });

            device.register();
        })
        .catch(function(err) {
            if (gen !== setupGen) return;  // an abandoned setup — the current one owns the state
            settingUp = false;
            clearSetupTimer();
            if (err.status === 403) {
                // Not enabled (revoked mid-session): no backoff, and a queued click fails now, not
                // after the 20s queue wait that would page on-call.
                warn('token fetch 403 — the dialer is not enabled for this account. Dock left idle.');
                if (queuedDial) {
                    queuedDial = null;
                    if (queueTimer) { clearTimeout(queueTimer); queueTimer = null; }
                    setStatus('Dialer unavailable');
                    window.setTimeout(function() { if (!activeAttemptId && !wrapup) resetIdle(); }, 4000);
                } else {
                    setIdleStatus('Dialer unavailable');
                }
                return;
            }
            warn('token fetch FAILED —', err.message, '— retrying');
            lastDeviceError = err;
            setIdleStatus('Reconnecting…');
            scheduleRebuild();
        });
    }

    // Throw the Device away and set up a new one. Never mid-call — the live Call belongs to it.
    function rebuildDevice(reason) {
        if (activeCall || settingUp) return;
        if (rebuildTimer) { clearTimeout(rebuildTimer); rebuildTimer = null; }
        log('rebuilding Twilio Device —', reason);
        deviceReady = false;
        if (device) {
            var old = device;
            device = null;
            old.removeAllListeners();  // its 'unregistered' on destroy must not schedule another rebuild
            try { old.destroy(); } catch (e) { warn('Device destroy failed —', e); }
        }
        setupDevice();
    }

    // Rebuild on backoff (2s, 4s, 8s … capped at a minute). After three failed rebuilds the dock
    // tells the rep to check their internet and pages on-call once, but keeps retrying.
    function scheduleRebuild() {
        if (rebuildTimer || !tokenUrl) return;
        var delay = Math.min(60000, 2000 * Math.pow(2, rebuildFailures));
        rebuildFailures += 1;
        if (rebuildFailures === 4) {
            setIdleStatus('Dialer offline — check your internet');
            reportError('device', lastDeviceError || 'Device would not reconnect', null, true);
        }
        rebuildTimer = window.setTimeout(function() {
            rebuildTimer = null;
            // The SDK re-registers by itself after a brief signaling drop — keep that Device.
            if (device && device.state === 'registered' && !tokenStale()) return;
            rebuildDevice('scheduled after error');
        }, delay);
    }

    function tokenStale() {
        return Date.now() - tokenFetchedAt > TOKEN_MAX_AGE_MS;
    }

    // Tab back in view / network back: the timers that renew the token may not have run while
    // the laptop slept, so rebuild now if the Device isn't registered or its token is old.
    function checkHealth(why) {
        // Not with the audio panel open — its mic stream lives on this Device's AudioHelper.
        if (!tokenUrl || activeCall || dialInFlight || settingUp || previewMode) return;
        if (device && device.state === 'registered' && !tokenStale()) return;
        rebuildDevice(why);
    }

    function refreshToken() {
        fetchToken(tokenUrl)
        .then(function(data) {
            if (data && data.token && device) {
                device.updateToken(data.token);
                tokenFetchedAt = Date.now();
            }
        })
        .catch(function(err) {
            warn('token refresh failed —', err.message, '— will rebuild');
            lastDeviceError = err;
            scheduleRebuild();
        });
    }

    // A call clicked while the Device was down is held here and placed the moment it registers,
    // so the rep clicks once. Dropped if the Device isn't back within DIAL_QUEUE_MS.
    function queueDial(args) {
        queuedDial = { args: args, at: Date.now() };
        showDock();
        setContext(args[2] || 'Dialer');
        setStatus('Reconnecting…');
        if (queueTimer) clearTimeout(queueTimer);
        queueTimer = window.setTimeout(function() {
            queueTimer = null;
            if (!queuedDial) return;
            queuedDial = null;
            warn('Device did not come back in time — dropping the queued call');
            setStatus('Couldn\'t connect — check your internet');
            reportError('dial', lastDeviceError || 'Device not ready when the call was clicked', null, true);
            window.setTimeout(function() { if (!activeAttemptId && !wrapup) resetIdle(); }, 4000);
        }, DIAL_QUEUE_MS);
        if (!settingUp) rebuildDevice('call clicked while the Device was down');
    }

    function runQueuedDial() {
        if (!queuedDial) return;
        var q = queuedDial;
        queuedDial = null;
        if (queueTimer) { clearTimeout(queueTimer); queueTimer = null; }
        log('Device back — placing the queued call');
        startCall.apply(null, q.args);
    }

    // ── Call lifecycle ──

    function startCall(contactPhonePk, leadPk, label, leasingLeadPk, occupancyPk, tenantPhone, callContext, dispoLeadPk) {
        log('startCall: contact_phone_pk =', contactPhonePk, '| lead_pk =', leadPk || '(none)',
            '| dispo_lead_pk =', dispoLeadPk || '(none)',
            '| leasing_lead_pk =', leasingLeadPk || '(none)', '| occupancy_pk =', occupancyPk || '(none)');
        if (activeCall || dialInFlight) { warn('a call is already in progress — ignoring this click'); return; }
        if (wrapup) { warn('finish wrap-up before starting a new call — ignoring this click'); return; }
        if (!tokenUrl) {
            warn('no token URL on the dock — dialer is not enabled here');
            setStatus('Dialer unavailable');
            showDock();
            return;
        }
        // Down, or up on a token old enough that the renewal timer may have been missed while
        // the laptop slept: rebuild and place this call once the Device registers.
        if (!device || !deviceReady || tokenStale()) {
            warn('Device not ready { device:', !!device, ', deviceReady:', deviceReady,
                 ', tokenStale:', tokenStale(), '} — rebuilding and queueing the call');
            queueDial([].slice.call(arguments));
            return;
        }

        // Fresh call — clear any leftover wrap-up state and capture context.
        resetCallState();
        activeLabel = label || '';
        activeContactPhonePk = contactPhonePk || null;
        activeLeadPk = leadPk || null;
        activeDispoLeadPk = dispoLeadPk || null;
        activeLeasingLeadPk = leasingLeadPk || null;
        activeOccupancyPk = occupancyPk || null;
        activeTenantPhone = tenantPhone || null;
        activeCallContext = callContext || null;
        setContext(activeLabel || 'Dialer');
        // Next step only applies when we have an acqs lead/prospect in context
        // (it's an acqs work-band feature; leasing and tenant calls have no next-action).
        syncNextStep();

        // An acqs call loads its lines first (the From select); leasing and tenant calls don't.
        if (activeContactPhonePk) {
            loadCallLines();
            return;
        }
        continueToDial();
    }

    // ── From switch (acqs calls) ──

    // Fetch the lines this acqs call may go out on, fill the device panel's From
    // select (shown only with two or more), then open the panel.  A failed fetch
    // opens it with no select and dials without a line_id: call_start then uses the
    // start line, or refuses naming the missing setting.
    function loadCallLines() {
        var seq = ++lineFetchSeq;
        var params = new URLSearchParams();
        params.set('contact_phone_pk', activeContactPhonePk);
        if (activeLeadPk) params.set('lead_pk', activeLeadPk);
        if (activeDispoLeadPk) params.set('dispo_lead_pk', activeDispoLeadPk);
        showDock();
        setStatus('Connecting…');
        fetch('/telephony/calls/lines/?' + params.toString(), { credentials: 'same-origin' })
        .then(function(r) {
            if (!r.ok) throw new Error('calls/lines HTTP ' + r.status);
            return r.json();
        })
        .then(function(data) {
            if (seq !== lineFetchSeq) return;  // a newer click took over
            callLines = data.lines || [];
            activeLineId = callLines.length ? String(callLines[0].id) : null;
            if (callLines.length > 1) {
                fillLineSelect();     // the header From line waits for Dial
            } else {
                showFrom();
            }
            continueToDial();
        })
        .catch(function(err) {
            if (seq !== lineFetchSeq) return;
            warn('could not load the From lines —', err.message, '— dialing on the start line');
            continueToDial();
        });
    }

    function lineLabel(lineId) {
        for (var i = 0; i < callLines.length; i++) {
            if (String(callLines[i].id) === String(lineId)) return callLines[i].label;
        }
        return '';
    }

    // Header "From: <line>" for the call about to be placed.
    function showFrom() {
        if (!fromEl) return;
        var label = lineLabel(activeLineId);
        fromEl.textContent = label ? 'From: ' + label : '';
        fromEl.hidden = !label;
    }

    function fillLineSelect() {
        if (!lineSelect) return;
        lineSelect.innerHTML = '';
        callLines.forEach(function(line) {
            var opt = document.createElement('option');
            opt.value = String(line.id);
            opt.textContent = line.label;
            lineSelect.appendChild(opt);
        });
        lineSelect.value = activeLineId;
    }

    // Open the device panel for the call being started.  Nothing is posted until
    // Dial: compliance (Iteration 4) is enforced server-side at call_start —
    // proceedToDial() shows the is-compliance warn surface when the response is
    // 200 {requiresOverride} (federal DNC) or refuses on a 400 hard block.
    function continueToDial() {
        pendingCall = {
            contactPhonePk: activeContactPhonePk,
            leadPk: activeLeadPk,
            dispoLeadPk: activeDispoLeadPk,
            label: activeLabel,
        };
        showDock();
        openPreview('dial');
    }

    // Everything from the CallAttempt POST through device.connect() — shared by
    // the device panel's Dial button and the compliance surface's Call anyway.
    // Reads the call context off the activeLabel/activeContactPhonePk/
    // activeLeadPk module state that startCall populated.
    function proceedToDial(override) {
        var contactPhonePk = activeContactPhonePk;
        var leadPk = activeLeadPk;
        var dispoLeadPk = activeDispoLeadPk;
        var leasingLeadPk = activeLeasingLeadPk;
        var occupancyPk = activeOccupancyPk;
        var tenantPhone = activeTenantPhone;
        var callContext = activeCallContext;

        // Leave the preview/compliance surfaces (if we came through one) before
        // going active. keepInput: the call we're about to place needs the
        // selected mic stream.
        closePreview(true);
        pendingCall = null;
        previewMode = null;
        if (dock) { dock.classList.remove('is-preview', 'is-compliance'); }

        showDock();
        setStatus('Connecting…');

        var body = new URLSearchParams();
        // Leasing leads have no AcqContactPhone — the server keys off the lead pk
        // and reads the number off the lead (anti-spoof). Tenant calls key off the
        // occupancy pk, with an optional phone + call_context the server validates
        // against the occupancy/tenant records. Acqs keys off the phone.
        if (leasingLeadPk) {
            body.set('leasing_lead_pk', leasingLeadPk);
        } else if (occupancyPk) {
            body.set('occupancy_pk', occupancyPk);
            if (tenantPhone) body.set('phone', tenantPhone);       // omit when absent — server dials Occupancy.phone
            if (callContext) body.set('call_context', callContext);
        } else {
            body.set('contact_phone_pk', contactPhonePk);
            if (leadPk) body.set('lead_pk', leadPk);  // omit when absent (don't send "null")
            if (dispoLeadPk) body.set('dispo_lead_pk', dispoLeadPk);  // buyer lead sheet only
            if (activeLineId) body.set('line_id', activeLineId);  // the From select; else the start line
        }
        if (override) body.set('override', 'true');  // rep confirmed a DNC / opt-out warning
        // Audio diagnostics (Loop #1427): what this call is about to send with.
        body.set('mic_label', micLabel());
        body.set('speaker_label', speakerLabel());
        body.set('client_info', JSON.stringify(callClientInfo()));
        startDiagnostics();

        log('POST /telephony/calls/start/', body.toString());
        postForm('/telephony/calls/start/', body)
        .then(function(r) {
            // Parse the body regardless of status — the compliance verdict
            // (400 {blocked} / 200 {requiresOverride}) travels in the JSON.
            return r.json().catch(function() { return {}; }).then(function(data) {
                return { ok: r.ok, status: r.status, data: data };
            });
        })
        .then(function(res) {
            var data = res.data || {};
            if (!res.ok) {
                // Hard compliance block (stale page / forced dial) — refuse, no dial.
                if (res.status === 400 && data.blocked) {
                    log('dial refused by server (hard block) —', data.error);
                    showComplianceBlocked(data.error);
                    return null;
                }
                // A refusal the rep can act on (no line configured, a line they may not use).
                if (data.error) toast(data.error, 'error');
                throw new Error('calls/start HTTP ' + res.status + ' — ' + (data.error || '(no body)'));
            }
            // Federal-DNC warn — pause and make the rep confirm before dialing.
            if (data.requiresOverride) {
                log('federal-DNC warning — awaiting rep override');
                showComplianceWarn(data.warning);
                return null;
            }
            if (!data.callAttemptId) throw new Error('calls/start returned no callAttemptId');
            log('CallAttempt', data.callAttemptId, 'created — connecting via Twilio Device');
            // Cleared to dial — NOW enable in-call controls and connect.
            setActive(true);
            setMuteUi(false);
            // Remember the attempt PK so an answer (SDK accept or WS `call_answered`) can match
            // this dock's active call before flipping to the in-call timer.
            activeAttemptId = String(data.callAttemptId);
            activeContactId = data.contactId || null;
            renderOutcomes(data.outcomes);
            makeMainOutcomes = data.makeMainOutcomes || [];
            dialRetried = false;
            connectAttempt();
        })
        .catch(function(err) {
            warn('could not start the call —', err.message);
            setStatus('Could not place call');
            // No CallAttempt to wrap up if start failed before activeAttemptId set.
            window.setTimeout(function() {
                if (activeAttemptId) enterWrapup(); else resetIdle();
            }, 2000);
        });
    }

    // device.connect() for the active attempt, then wire the Call's events. A failure before the
    // call reaches Twilio goes to failDial, which rebuilds the Device and retries once.
    function connectAttempt() {
        var attemptId = activeAttemptId;
        dialInFlight = true;
        if (dialWatchdog) clearTimeout(dialWatchdog);
        // A dead websocket can leave connect() pending with no error at all.
        // If the server says Twilio did get it, the call is just slow to ring — leave it alone.
        dialWatchdog = window.setTimeout(function() {
            dialWatchdog = null;
            var err = { code: 'timeout', message: 'no ring from Twilio after ' + (DIAL_WATCHDOG_MS / 1000) + 's' };
            reportError('dial', err, attemptId, false).then(function(res) {
                if (!dialInFlight || attemptId !== activeAttemptId) return;
                if (res.reachedTwilio) reachedTwilio(); else failDial(err);
            });
        }, DIAL_WATCHDOG_MS);
        applyCallInputDevice().then(function() {
            return device.connect({ params: { CallAttemptId: String(attemptId) } });
        })
        .then(function(call) {
            if (!dialInFlight || attemptId !== activeAttemptId) {
                // The dock gave up (or retried) while connect() was pending — drop this stray Call.
                log('discarding a late Call for attempt', attemptId);
                call.disconnect();
                return;
            }
            activeCall = call;
            ringbackHeard = false;
            setActive(true);
            setStatus('Calling…');
            paintDeviceRow();
            log('Twilio Device connected — Twilio is now calling the TwiML endpoint');

            // In-call mic bar + silent-mic check (§ In-call device row + silent-mic check).
            call.on('volume', function(inputVolume) {
                if (call !== activeCall) return;
                onCallVolume(inputVolume);
            });
            call.on('warning', function(name) {
                if (call !== activeCall) return;
                onCallWarning(name);
            });
            call.on('warning-cleared', function(name) {
                if (call !== activeCall) return;
                onCallWarningCleared(name);
            });

            call.on('ringing', function() {
                if (call !== activeCall) return;
                reachedTwilio();
                log('call ringing');
                setStatus('Ringing…');
                startRingback(call);
            });
            call.on('accept', function() {
                if (call !== activeCall) return;
                reachedTwilio();
                // voice_twiml's <Dial answerOnBridge> holds the browser leg until
                // the callee answers, so 'accept' IS the real answer. On acqs pages
                // the WS call_answered event may arrive too; onAnswered is idempotent.
                log('callee answered (accept)');
                if (diag && !diag.answerAt) diag.answerAt = Date.now();
                recordCallEvent('accept');
                startMicCheck();
                startCallAudioWatch();
                onAnswered(activeAttemptId);
            });
            // Network trouble (Iteration 6a). `reconnecting`/`reconnected` cover
            // the transient blip; a network `error` (or a disconnect that arrives
            // while we're already in trouble) is the hard drop → "dropped".
            call.on('reconnecting', function(err) {
                if (call !== activeCall) return;
                log('call reconnecting —', (err && err.code), (err && err.message) || '');
                recordCallEvent('reconnecting', { code: err && err.code });
                showReconnecting();
            });
            call.on('reconnected', function() {
                if (call !== activeCall) return;
                log('call reconnected');
                recordCallEvent('reconnected');
                clearReconnecting();
            });
            call.on('disconnect', function() {
                if (call !== activeCall) return;
                log('call disconnected', netTrouble ? '(after network trouble)' : '');
                recordCallEvent('disconnect', netTrouble ? { network: true } : null);
                // A disconnect that lands while we're mid-reconnect (or after a
                // network error) is a drop, not a clean hangup — surface it.
                if (netTrouble) showDropped();
                enterWrapup();
            });
            call.on('cancel', function() { if (call !== activeCall) return; log('call canceled'); enterWrapup(); });
            call.on('reject', function() { if (call !== activeCall) return; log('call rejected'); enterWrapup(); });
            call.on('error', function(err) {
                if (call !== activeCall) return;
                var code = err && err.code;
                warn('call error:', code, (err && err.message) || err, err);
                recordCallEvent('error', { code: code });
                // Failed before Twilio ever answered (no CallSid yet) and not a far-end refusal:
                // the browser side broke, so rebuild and retry rather than wrap up a call that
                // never happened.
                if (dialInFlight && !(call.parameters && call.parameters.CallSid) && !farEndStatus(err) &&
                        code !== 31401 && code !== 31402) {
                    failDial(err);
                    return;
                }
                // The SDK emits this error just before its disconnect, and the
                // disconnect's enterWrapup sets "Wrap-up" — so set the reason after it.
                var farEnd = farEndStatus(err);
                if (farEnd) {
                    window.setTimeout(function() {
                        enterWrapup();
                        if (wrapup) setStatus(farEnd);
                    }, 0);
                    return;
                }
                if (isNetworkError(code)) {
                    // The call is ending for network reasons. MARK it (netTrouble)
                    // so the disconnect that follows shows "dropped — network"
                    // instead of a clean wrap-up — even if `reconnecting` already
                    // fired and the banner is up, this keeps the flag set through
                    // to the disconnect. Show the banner now if nothing's up yet.
                    netTrouble = true;
                    if (netBannerEl && netBannerEl.hidden) showReconnecting();
                    return;
                }
                // 31401/31402: the SDK could not get the mic. The call ends
                // (disconnect → wrap-up) before this error fires, so the reason
                // set here stays on the wrap-up status line until Save/Skip.
                stopRingback();  // wrap-up is 1.5s away; don't ring under "Call failed"
                dialFailed = true;
                var mic = (code === 31401 || code === 31402)
                    ? (micError(callMicError) || micError(err))
                    : null;
                if (mic) {
                    setStatus(mic.status);
                    toast(mic.message, 'error');
                } else {
                    setStatus('Call failed');
                }
                window.setTimeout(enterWrapup, 1500);
            });
        })
        .catch(function(err) {
            if (attemptId !== activeAttemptId) return;  // a stale connect() from an abandoned attempt
            failDial(err);
        });
    }

    // Twilio has the call (it rang or answered) — from here a failure is a real call failure.
    function reachedTwilio() {
        dialInFlight = false;
        if (dialWatchdog) { clearTimeout(dialWatchdog); dialWatchdog = null; }
    }

    // The dial failed before Twilio confirmed it. Ask the server whether the attempt reached
    // Twilio's webhook: if not, rebuild the Device and re-dial the same attempt once; on the
    // second failure give up, which marks the attempt failed and pages on-call.
    function failDial(err) {
        if (!dialInFlight) return;
        var attemptId = activeAttemptId;
        dialInFlight = false;
        if (dialWatchdog) { clearTimeout(dialWatchdog); dialWatchdog = null; }
        var stray = activeCall;
        stopCallAudioWatch();
        activeCall = null;  // its trailing disconnect/error events are now ignored
        stopMicCheck();
        if (stray) { try { stray.disconnect(); } catch (e) { /* already gone */ } }
        warn('dial failed before reaching Twilio —', (err && err.code), (err && err.message) || err);
        stopRingback();
        setStatus('Reconnecting…');

        var retry = !dialRetried;
        reportError('dial', err, attemptId, !retry).then(function(res) {
            if (attemptId !== activeAttemptId) return;  // rep moved on
            if (res.reachedTwilio !== false) {
                // Twilio got it, or the check itself failed so we can't tell — never re-dial on
                // a maybe. A real call failure: normal wrap-up.
                setStatus('Call failed');
                dialFailed = true;
                window.setTimeout(function() { if (attemptId === activeAttemptId) enterWrapup(); }, 1500);
                return;
            }
            if (retry) {
                dialRetried = true;
                log('retrying attempt', attemptId, 'on a fresh Device');
                dialInFlight = true;  // hold the slot so a click can't start a second call
                retryAfterRebuild(attemptId);
                return;
            }
            setStatus('Couldn\'t connect — check your internet');
            toast('Call didn\'t go through — check your internet and try again', 'error');
            window.setTimeout(function() { if (attemptId === activeAttemptId) resetIdle(); }, 4000);
        });
    }

    function retryAfterRebuild(attemptId) {
        deviceReady = false;
        var waited = 0;
        dialInFlight = false;       // let rebuildDevice/setupDevice run
        rebuildDevice('dial failed');
        dialInFlight = true;
        (function wait() {
            if (attemptId !== activeAttemptId || !dialInFlight) return;  // gave up or rep moved on
            if (deviceReady) { dialInFlight = false; connectAttempt(); return; }
            waited += 500;
            if (waited >= DIAL_QUEUE_MS) {
                dialInFlight = true;
                failDial(lastDeviceError || { message: 'Device did not come back for the retry' });
                return;
            }
            window.setTimeout(wait, 500);
        })();
    }

    // ── Preview / settings actions (Iteration 7) ──

    // Dial button in the preview footer:
    //   • 'dial' mode (a call is pending) → proceed to place it.
    //   • 'settings' mode (gear, no call) → it's a Save/Close — just close.
    // Selections are already applied+persisted on each <select> change, so the
    // button doesn't need to re-apply anything.
    function confirmPreview() {
        if (previewMode === 'dial' && pendingCall) {
            // The From select (multi-line acqs call) — its choice goes out as line_id.
            if (callLines.length > 1 && lineSelect && lineSelect.value) {
                activeLineId = lineSelect.value;
                log('From line chosen —', activeLineId);
                showFrom();
            }
            log('Dial pressed — proceeding to place the pending call');
            proceedToDial();
            return;
        }
        // Settings/standalone: just close the panel.
        log('preview closed (settings mode)');
        returnFromPreview();
    }

    // Cancel button in the preview footer: drop the pending call and go idle
    // (or back to the live call / wrap-up surface if one is in progress).
    function cancelPreview() {
        log('preview cancelled');
        pendingCall = null;
        previewMode = null;
        returnFromPreview();
    }

    // Close the device panel and return to whatever surface preceded it. When
    // that's a live call or wrap-up (gear opened mid-flow), restore the header
    // snapshot from openSettings — openPreview overwrote it with "Audio devices"
    // and the call/wrap-up surfaces don't repaint context+status themselves.
    function returnFromPreview() {
        if (activeCall || wrapup) {
            closePreview();
            if (dock) { dock.classList.remove('is-preview'); dock.classList.add(activeCall ? 'is-active' : 'is-wrapup'); }
            setContext(savedContext || activeLabel || 'Dialer');
            // Derive the current canonical status from live state rather than
            // restoring the snapshot — the call may have advanced (ringing →
            // in-call via onAnswered, or → wrap-up via enterWrapup) while the
            // settings panel was open, making savedStatus stale.
            if (wrapup) {
                setStatus('Wrap-up');
            } else if (activeCall) {
                // timerInterval running means onAnswered already fired.
                setStatus(timerInterval !== null ? 'In call' : (savedStatus || 'Calling…'));
            }
        } else {
            // resetIdle() calls closePreview() internally — don't double-call.
            resetIdle();
        }
    }

    // Gear in the controls — re-open the device panel any time. Works mid-call
    // (device changes apply live + persist); with no call it's a standalone
    // settings panel whose Dial button reads "Save".
    function openSettings() {
        // The dial panel is already the device panel; reopening it as settings
        // would turn Dial into Save and drop the pending call.
        if (previewMode === 'dial') return;
        // Snapshot the header before openPreview overwrites it with "Audio
        // devices" — the live-call / wrap-up surfaces we may return to on
        // Close/Cancel don't otherwise repaint the context + status, so without
        // this the gear leaves the header stuck on "Audio devices".
        savedContext = contextEl ? contextEl.textContent : '';
        savedStatus = statusEl ? statusEl.textContent : '';
        showDock();
        openPreview('settings');
    }

    // ── Compliance warn surface (Iteration 4) ──

    // Switch the dock to the pre-dial compliance state. Shared by both the
    // federal-DNC override warning and the hard-block notice; `allowOverride`
    // toggles the "Call anyway" button (present for federal-DNC, hidden for a
    // hard block where no override exists).
    function showCompliance(message, allowOverride) {
        closePreview(true);
        if (complianceTextEl) complianceTextEl.textContent = message || 'This number is restricted.';
        if (complianceCallBtn) complianceCallBtn.hidden = !allowOverride;
        if (dock) {
            dock.classList.remove('is-idle', 'is-preview', 'is-active', 'is-wrapup', 'is-keypad');
            dock.classList.add('is-compliance');
        }
        setStatus('Compliance check');
        showDock();
    }

    // Federal-DNC (not hard-blocked): warn + offer Call anyway / Cancel.
    function showComplianceWarn(warning) {
        showCompliance(warning || 'This number is on the federal Do Not Call registry.', true);
    }

    // Server hard block (stale page / forced dial): explain why, no override.
    function showComplianceBlocked(reason) {
        showCompliance(reason || 'This number can’t be called.', false);
    }

    // Bad number: Save sets the called phone's status through the existing contact-phone-update
    // endpoint and live-refreshes the on-page card. Save shows the one toast.
    function postNumberStatus(status, contactPhonePk) {
        var pb = new URLSearchParams();
        pb.set('status', status);
        log('POST number status', status, '→ phone', contactPhonePk);
        return postForm('/acqs/contact-phones/' + contactPhonePk + '/update/', pb)
        .then(function(pr) {
            if (!pr.ok) throw new Error('contact-phone-update HTTP ' + pr.status);
            // The dock is a global widget posting via plain fetch, so the on-page
            // owner/contact card never sees the response's HX-Trigger events.
            // Re-fire them on <body> so the card self-refreshes (status badge +
            // call-button state) — but ONLY when the phone we changed is actually
            // on the current page (the persistent dock may have outlived navigation
            // to a different record). Scoped GET, no toast → no double feedback.
            var markedPhoneOnPage = document.querySelector(
                '[hx-post*="contact-phones/' + contactPhonePk + '/update/"]');
            if (window.htmx && markedPhoneOnPage) {
                window.htmx.trigger(document.body, 'ownerCardChanged');
                window.htmx.trigger(document.body, 'contactUpdated');
            }
        });
    }

    // Called on the SDK 'accept' (real answer, via <Dial answerOnBridge>) and by
    // the acqs WS layer when the server reports the callee leg answered
    // (Twilio <Number> child in-progress). Only flips to the in-call timer if
    // the answered attempt matches THIS dock's active call — so a stale or
    // cross-tab event can't start a timer on the wrong call.
    function onAnswered(attemptId) {
        if (!activeCall || activeAttemptId == null) return;
        if (String(attemptId) !== String(activeAttemptId)) {
            log('onAnswered: attempt', attemptId, '≠ active', activeAttemptId, '— ignoring');
            return;
        }
        stopRingback();
        if (timerInterval !== null) return;  // already running — idempotent
        log('answered — starting in-call timer');
        setStatus('In call');
        startTimer();
    }

    function hangup() {
        if (activeCall) {
            // An explicit hang-up is always a clean end — even mid-blip. Clear the
            // network-trouble flag first so the disconnect handler doesn't mislabel
            // it "dropped — network".
            netTrouble = false;
            hideNetBanner();
            // disconnect() fires the 'disconnect' event which calls enterWrapup()
            activeCall.disconnect();
        } else if (wrapup) {
            // Already wrapping up — hangup button is a no-op; use Save (Skip shows only after a failed call).
            return;
        } else {
            if (device) device.disconnectAll();
            resetIdle();
        }
    }

    // ── Wrap-up ──

    // Next step stays hidden if this fails; the rep saves with a call result alone.
    function loadNextStep(attemptId) {
        if (!activeLeadPk) return;
        fetch('/telephony/calls/' + attemptId + '/next-step/', { credentials: 'same-origin' })
        .then(function(r) {
            if (!r.ok) throw new Error('next-step HTTP ' + r.status);
            return r.json();
        })
        .then(function(info) {
            if (attemptId === activeAttemptId) renderNextStep(info);
        })
        .catch(function(err) { warn('could not load next step —', err.message); });
    }

    // Transition from a live/just-ended call into wrap-up mode. Keeps the
    // notes + outcome the rep set during the call; the live call ends but the
    // CallAttempt id and lead/phone context are preserved for the Save POST.
    function enterWrapup() {
        stopRingback();
        if (wrapup) return;  // idempotent — disconnect can fire more than once
        dialInFlight = false;
        if (dialWatchdog) { clearTimeout(dialWatchdog); dialWatchdog = null; }
        stopCallAudioWatch();  // posts silent-end if the call ended still silent
        sendDiagnostics();
        activeCall = null;
        stopMicCheck();
        closeDevicePickers();
        stopTimer();
        setMuteUi(false);
        clearKeypad();  // wrap-up always shows notes/outcome/footer, never the keypad
        // Disable in-call controls; the call is over.
        if (hangupBtn) hangupBtn.disabled = true;
        if (muteBtn) muteBtn.disabled = true;
        if (keypadToggleBtn) keypadToggleBtn.disabled = true;

        if (!activeAttemptId) {
            // Nothing to log against — just go idle.
            resetIdle();
            return;
        }
        wrapup = true;
        if (dock) {
            dock.classList.remove('is-active');
            dock.classList.add('is-wrapup');
        }
        if (wrapupHintEl) wrapupHintEl.hidden = false;
        if (nextDateEl) nextDateEl.min = isoDate(new Date());
        loadNextStep(activeAttemptId);
        syncWrapupActions();
        setStatus('Wrap-up');
        log('entered wrap-up for attempt', activeAttemptId);
    }

    function saveWrapup() {
        // Save/Skip are wrap-up-only — never let them fire while the call is live
        // (the footer is CSS-hidden during is-active, this is the belt-and-braces).
        if (activeCall) { warn('cannot save — call still active; hang up first'); return; }
        if (!activeAttemptId) { resetIdle(); return; }
        if (!wrapupSavable()) return;
        var notes = notesEl ? notesEl.value.trim() : '';
        var attemptId = activeAttemptId;
        var leadPk = activeLeadPk;
        var outcome = selectedOutcome;
        // A blank save from an errored call writes no next step, even if a chip is on.
        var complete = wrapupComplete();
        var naVals = complete ? nextActionValues() : null;
        var phoneStatus = selectedOutcome === 'bad_number' ? selectedPhoneStatus : null;
        var bookUrl = complete && selectedOutcome !== 'bad_number' && nextStep === 'book' && nextStepInfo
            ? nextStepInfo.bookUrl : '';
        var contactPhonePk = activeContactPhonePk;
        var contactId = activeContactId;
        var makeMain = !!(makeMainWrap && !makeMainWrap.hidden && makeMainEl && makeMainEl.checked);

        if (saveBtn) saveBtn.disabled = true;

        // 1) Primary: the call outcome + notes (single timeline entry server-side).
        var body = new URLSearchParams();
        body.set('outcome', outcome || '');
        body.set('outcome_notes', notes);
        if (makeMain) body.set('make_main', '1');
        log('POST /telephony/calls/' + attemptId + '/outcome/', body.toString());
        postForm('/telephony/calls/' + attemptId + '/outcome/', body)
        .then(function(r) {
            if (!r.ok) {
                return r.text().then(function(t) {
                    throw new Error('outcome HTTP ' + r.status + ' — ' + (t || '(no body)'));
                });
            }
            return r.json();
        })
        .then(function() {
            // The outcome saved. Fire the conditional secondary POSTs and wait
            // for all of them so we can show exactly ONE toast at the end.
            var secondary = [];
            var statusFailed = false;

            // The phone's status stays its own field on the phone (`postNumberStatus`). The call
            // is already saved, so a failure here doesn't hold the rep in wrap-up.
            if (phoneStatus && contactPhonePk) {
                secondary.push(postNumberStatus(phoneStatus, contactPhonePk).catch(function(err) {
                    warn('could not save phone status —', err.message);
                    statusFailed = true;
                }));
            }

            // 2) Next action — only when a lead/prospect is in context.
            if (leadPk && naVals) {
                // The dock is PERSISTENT — the rep may have browsed to a different
                // record mid-call. The save must always go to the record we CALLED
                // (leadPk == activeLeadPk), never to whatever happens to be on screen.
                // We only live-swap the on-screen "Outreach" work-band when it
                // actually belongs to the called record — matched by the pk in the
                // band's own next-action URL (leading+trailing slash avoid 16-vs-316
                // substring collisions). When it matches, drive the save through HTMX
                // targeting that band so the POST response refreshes it in place
                // (works in a full detail OR an open side-sheet); `X-Dialer-Wrapup`
                // suppresses its "Next action saved" toast (the dock shows one
                // consolidated "Call logged"). Otherwise (browsed away, or calling
                // from a list with no band) just persist to the called record.
                var bands = Array.prototype.slice.call(
                    document.querySelectorAll('[data-next-action-url]')
                );
                var matchesCalledLead = function(candidate) {
                    var actionUrl = candidate.getAttribute('data-next-action-url') || '';
                    return actionUrl.indexOf('/' + leadPk + '/next-action/') !== -1;
                };
                // A sheet is appended after the underlying page. Prefer its band
                // when both surfaces show the same Lead, then fall back to the page.
                var band = bands.find(function(candidate) {
                    return candidate.closest('.sheet.show') && matchesCalledLead(candidate);
                }) || bands.find(matchesCalledLead);
                var bandUrl = band ? band.getAttribute('data-next-action-url') : '';
                var bandIsCalled = bandUrl.indexOf('/' + leadPk + '/next-action/') !== -1;
                // lead_next_action accepts both lead- and prospect-pipeline rows, so
                // it's the safe universal save URL when we're NOT swapping the band.
                var fallbackUrl = '/acqs/leads/' + leadPk + '/next-action/';

                if (band && bandIsCalled && window.htmx) {
                    // htmx.ajax resolves on a 4xx too, so watch for its error event.
                    var bandFailed = false;
                    var onBandError = function() { bandFailed = true; };
                    document.body.addEventListener('htmx:responseError', onBandError);
                    secondary.push(
                        window.htmx.ajax('POST', bandUrl, {
                            target: '#' + band.id, swap: 'outerHTML',
                            values: naVals, headers: { 'X-Dialer-Wrapup': '1' },
                        }).finally(function() {
                            document.body.removeEventListener('htmx:responseError', onBandError);
                        }).then(function() {
                            if (bandFailed) throw new Error('next-action band POST failed');
                        })
                    );
                } else {
                    secondary.push(
                        postForm(fallbackUrl, new URLSearchParams(naVals)).then(function(nr) {
                            if (!nr.ok) throw new Error('next-action HTTP ' + nr.status);
                        })
                    );
                }
            }

            return Promise.all(secondary).then(function() {
                // Happy path: exactly one toast no matter how many POSTs ran.
                if (statusFailed) toast('Call logged, but the phone status did not save', 'error');
                else toast('Call logged', 'success');
                // The outcome is the phone's latest call result (and maybe its new
                // main number), so let the Contacts block refresh — scoped, like
                // postNumberStatus, to the called phone being on this page. Acqs
                // only: leasing/tenant calls have no contactId.
                var calledPhoneOnPage = contactPhonePk && document.querySelector(
                    '[data-dialer-call][data-contact-phone-pk="' + contactPhonePk + '"]');
                if (window.htmx && contactId && calledPhoneOnPage) {
                    window.htmx.trigger(document.body, 'contactUpdated', { contactId: contactId });
                }
                resetIdle();
                if (bookUrl && window.rfSheet) window.rfSheet.open(bookUrl);
            });
        })
        .catch(function(err) {
            warn('could not save call —', err.message);
            toast('Could not save call', 'error');
            if (saveBtn) saveBtn.disabled = false;
        });
    }

    function dismissWrapup() {
        // Skip is wrap-up-only — don't let it tear down the dock mid-call.
        if (activeCall) { warn('cannot skip — call still active; hang up first'); return; }
        if (!dialFailed) return;
        log('wrap-up dismissed without saving');
        resetIdle();
    }

    // ── Reset ──

    // Clear per-call selections + form fields (used on fresh call + full reset).
    // NOT called by enterWrapup — so a "dropped — network" banner survives into
    // wrap-up and is only cleared here, once the rep starts fresh or goes idle.
    function resetCallState() {
        stopCallAudioWatch();  // before activeAttemptId clears — a silent-end report names the attempt
        sendDiagnostics();     // a call that never reached wrap-up (a failed dial); no-op once sent
        diag = null;
        hideNetBanner();
        activeCall = null;
        stopMicCheck();
        activeAttemptId = null;
        activeLabel = '';
        activeContactPhonePk = null;
        activeLeadPk = null;
        activeDispoLeadPk = null;
        activeLeasingLeadPk = null;
        activeOccupancyPk = null;
        activeTenantPhone = null;
        activeCallContext = null;
        callLines = [];
        activeLineId = null;
        lineFetchSeq++;  // drop a From-lines fetch still in flight
        if (fromEl) { fromEl.textContent = ''; fromEl.hidden = true; }
        selectedOutcome = null;
        dialFailed = false;
        activeContactId = null;
        makeMainOutcomes = [];
        nextActionDays = null;
        nextStep = null;
        selectPhoneStatus(null);
        if (badNumberWrap) badNumberWrap.hidden = true;
        if (nextDateEl) nextDateEl.value = '';
        renderNextStep(null);
        syncNextStep();
        if (makeMainEl) makeMainEl.checked = true;  // checked by default on every call
        if (makeMainWrap) makeMainWrap.hidden = true;
        if (notesEl) notesEl.value = '';
        if (nextActionTextEl) nextActionTextEl.value = '';
        renderOutcomes([]);  // the next calls/start supplies the next call's set
        if (nextActionWrap) {
            nextActionWrap.querySelectorAll('.chip-active').forEach(function(c) {
                c.classList.remove('chip-active');
            });
        }
    }

    function resetIdle() {
        stopRingback();
        wrapup = false;
        dialInFlight = false;
        if (dialWatchdog) { clearTimeout(dialWatchdog); dialWatchdog = null; }
        queuedDial = null;
        if (queueTimer) { clearTimeout(queueTimer); queueTimer = null; }
        resetCallState();
        stopTimer();
        setActive(false);
        setMuteUi(false);
        clearKeypad();
        closePreview();            // drop the mic-meter gate + clear the meter
        pendingCall = null;
        previewMode = null;
        if (wrapupHintEl) wrapupHintEl.hidden = true;
        if (saveBtn) saveBtn.disabled = false;
        if (nextActionWrap) nextActionWrap.hidden = true;
        hideDock();
        setContext('Dialer');
        // Reflect device-registering state so the idle dock is honest about readiness.
        setStatus(deviceReady ? 'Ready' : (device ? 'Registering…' : 'Connecting…'));
    }

    // ── Init ──

    function init() {
        dock = document.getElementById('dialer-dock');
        if (!dock) {
            warn('no #dialer-dock on this page — dialer is gated off here',
                 '(needs an enabled telephony profile). Call buttons with a tel: href fall back to it.');
            return;
        }
        if (initialized) return; // persistent across HTMX swaps — init once
        initialized = true;
        log('init — dock found, setting up Twilio Device');

        statusEl = dock.querySelector('.dialer-dock__status');
        contextEl = dock.querySelector('[data-dialer-context]');
        timerEl = dock.querySelector('.dialer-dock__timer');
        hangupBtn = dock.querySelector('[data-dialer-hangup]');
        muteBtn = dock.querySelector('[data-dialer-mute]');
        keypadToggleBtn = dock.querySelector('[data-dialer-keypad-toggle]');
        keypadEl = dock.querySelector('[data-dialer-keypad]');
        notesEl = dock.querySelector('[data-dialer-notes]');
        outcomesEl = dock.querySelector('[data-dialer-outcomes]');
        nextActionWrap = dock.querySelector('[data-dialer-next-action]');
        nextActionTextEl = dock.querySelector('[data-dialer-next-action-text]');
        wrapupHintEl = dock.querySelector('[data-dialer-wrapup-hint]');
        saveBtn = dock.querySelector('[data-dialer-save]');
        dismissBtn = dock.querySelector('[data-dialer-dismiss]');

        // Audio-device preview / setup (Iteration 7).
        settingsBtn = dock.querySelector('[data-dialer-settings]');
        previewUnavailableEl = dock.querySelector('[data-dialer-preview-unavailable]');
        micSelect = dock.querySelector('[data-dialer-mic]');
        speakerSelect = dock.querySelector('[data-dialer-speaker]');
        ringerSelect = dock.querySelector('[data-dialer-ringer]');
        meterFillEl = dock.querySelector('[data-dialer-meter-fill]');
        outputFieldEls = dock.querySelectorAll('[data-dialer-output-fields]');
        outputNoteEl = dock.querySelector('[data-dialer-output-note]');
        speakerTestBtn = dock.querySelector('[data-dialer-speaker-test]');
        previewDialBtn = dock.querySelector('[data-dialer-preview-dial]');
        previewCancelBtn = dock.querySelector('[data-dialer-preview-cancel]');

        // From switch (acqs calls).
        fromEl = dock.querySelector('[data-dialer-from]');
        lineFieldEl = dock.querySelector('[data-dialer-line-field]');
        lineSelect = dock.querySelector('[data-dialer-line]');

        // Compliance warn surface + the Bad number and Next step fields.
        complianceTextEl = dock.querySelector('[data-dialer-compliance-text]');
        complianceCallBtn = dock.querySelector('[data-dialer-compliance-call]');
        complianceCancelBtn = dock.querySelector('[data-dialer-compliance-cancel]');
        badNumberWrap = dock.querySelector('[data-dialer-bad-number]');
        followUpWrap = dock.querySelector('[data-dialer-follow-up]');
        handOffWrap = dock.querySelector('[data-dialer-hand-off]');
        nextTextWrap = dock.querySelector('[data-dialer-next-text-field]');
        nextDateEl = dock.querySelector('[data-dialer-next-date]');
        handOffPersonEl = dock.querySelector('[data-dialer-hand-off-person]');
        makeMainWrap = dock.querySelector('[data-dialer-make-main-field]');
        makeMainEl = dock.querySelector('[data-dialer-make-main]');

        // Network status banner (Iteration 6a).
        netBannerEl = dock.querySelector('[data-dialer-netbanner]');
        netBannerIconEl = dock.querySelector('[data-dialer-netbanner-icon]');
        netBannerTextEl = dock.querySelector('[data-dialer-netbanner-text]');

        // In-call device row + mic banner (Loop #1427).
        micBannerEl = dock.querySelector('[data-dialer-micbanner]');
        micNameEl = dock.querySelector('[data-dialer-mic-name]');
        speakerNameEl = dock.querySelector('[data-dialer-speaker-name]');
        micPickerEl = dock.querySelector('[data-dialer-mic-picker]');
        micMenuEl = dock.querySelector('[data-dialer-mic-menu]');
        speakerDropdownEl = dock.querySelector('[data-dialer-speaker-dropdown]');
        speakerMenuEl = dock.querySelector('[data-dialer-speaker-menu]');
        speakerTextEl = dock.querySelector('[data-dialer-speaker-text]');
        callMeterFillEl = dock.querySelector('[data-dialer-call-meter-fill]');
        // A pick switches the live call and saves it as the default — the device
        // panel's own path (applyInputDevice / applyOutputDevice).
        if (micMenuEl) {
            micMenuEl.addEventListener('click', function(e) {
                var item = e.target.closest('[data-device-id]');
                if (!item) return;
                var id = item.getAttribute('data-device-id');
                if (micSelect) micSelect.value = id;
                applyInputDevice(id, micSelect);
            });
        }
        if (speakerMenuEl) {
            speakerMenuEl.addEventListener('click', function(e) {
                var item = e.target.closest('[data-device-id]');
                if (!item || !device || !device.audio) return;
                var id = item.getAttribute('data-device-id');
                if (speakerSelect) speakerSelect.value = id;
                applyOutputDevice(device.audio.speakerDevices, id, speakerSelect, 'speaker');
            });
        }
        // "Switch mic" opens the mic picker's menu (a sheet on phones). stopPropagation
        // keeps this click from reaching dropdowns.js's outside-click close. With the gear's panel
        // open mid-call the device row is hidden, so the mic select there is used.
        var micSwitchBtn = dock.querySelector('[data-dialer-mic-switch]');
        if (micSwitchBtn) {
            micSwitchBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                if (!dock.classList.contains('is-active')) {
                    if (micSelect) micSelect.focus();
                    return;
                }
                var dropdown = micPickerEl && micPickerEl.closest('.dropdown');
                if (dropdown && !dropdown.classList.contains('show')) micPickerEl.click();
            });
        }

        resetIdle();

        tokenUrl = dock.getAttribute('data-token-url');
        if (tokenUrl) setupDevice();
        document.addEventListener('visibilitychange', function() {
            if (document.visibilityState === 'visible') checkHealth('tab visible again');
        });
        window.addEventListener('online', function() { checkHealth('network back'); });
        window.addEventListener('pagehide', beaconDiagnostics, true);  // capture: before the Device's own (see beaconDiagnostics)

        // Delegated call-button handler — survives HTMX content swaps.
        document.addEventListener('click', function(e) {
            var btn = e.target.closest('[data-dialer-call]');
            if (!btn) return;
            e.preventDefault();
            var label = btn.getAttribute('data-dialer-label') || '';
            // Leasing button: keyed on the LeasingLead pk (no AcqContactPhone).
            var leasingLeadPk = btn.getAttribute('data-leasing-lead-pk');
            if (leasingLeadPk) {
                startCall(null, null, label, leasingLeadPk);
                return;
            }
            // Tenant button: keyed on the Occupancy pk, with an optional raw phone
            // + call_context (e.g. 'collections') the server cross-checks.
            var occupancyPk = btn.getAttribute('data-occupancy-pk');
            if (occupancyPk) {
                var tenantPhone = btn.getAttribute('data-tenant-phone');
                var callContext = btn.getAttribute('data-call-context');
                startCall(null, null, label, null, occupancyPk, tenantPhone, callContext);
                return;
            }
            var contactPhonePk = btn.getAttribute('data-contact-phone-pk');
            if (!contactPhonePk) {
                warn('call button has no data-contact-phone-pk / data-leasing-lead-pk / data-occupancy-pk — ignoring', btn);
                return;
            }
            // lead_pk is optional — contact/owner surfaces have no lead in context.
            // dispo_lead_pk is set only on the buyer lead sheet.
            var leadPk = btn.getAttribute('data-lead-pk');
            var dispoLeadPk = btn.getAttribute('data-dispo-lead-pk');
            startCall(contactPhonePk, leadPk, label, null, null, null, null, dispoLeadPk);
        });

        // Hangup button (lives inside the persistent dock).
        if (hangupBtn) {
            hangupBtn.addEventListener('click', function(e) { e.preventDefault(); hangup(); });
        }

        // Mute toggle.
        if (muteBtn) {
            muteBtn.addEventListener('click', function(e) { e.preventDefault(); toggleMute(); });
        }

        // Keypad show/hide + digit send (delegated within the keypad).
        if (keypadToggleBtn) {
            keypadToggleBtn.addEventListener('click', function(e) { e.preventDefault(); toggleKeypad(); });
        }
        if (keypadEl) {
            keypadEl.addEventListener('click', function(e) {
                var key = e.target.closest('[data-digit]');
                if (!key) return;
                e.preventDefault();
                sendDigit(key.getAttribute('data-digit'));
            });
        }

        // Compliance warn surface: Call anyway re-POSTs with override=true; Cancel
        // (also the hard-block Close) returns to idle. Mirrors the preview footer.
        if (complianceCallBtn) {
            complianceCallBtn.addEventListener('click', function(e) {
                e.preventDefault();
                proceedToDial(true);
            });
        }
        if (complianceCancelBtn) {
            complianceCancelBtn.addEventListener('click', function(e) { e.preventDefault(); resetIdle(); });
        }

        // Bad number's chips (single-select).
        if (badNumberWrap) {
            badNumberWrap.addEventListener('click', function(e) {
                var chip = e.target.closest('[data-phone-status]');
                if (!chip) return;
                e.preventDefault();
                var status = chip.getAttribute('data-phone-status');
                selectPhoneStatus(selectedPhoneStatus === status ? null : status);
                syncWrapupActions();
            });
        }

        // Outcome chips (single-select).
        bindOutcomeChips(outcomesEl);

        // Next-action quick-date chips.
        if (nextActionWrap) {
            nextActionWrap.addEventListener('click', function(e) {
                var stepChip = e.target.closest('[data-next-step]');
                if (stepChip) {
                    e.preventDefault();
                    var step = stepChip.getAttribute('data-next-step');
                    selectNextStep(nextStep === step ? null : step);
                    return;
                }
                var chip = e.target.closest('[data-next-days]');
                if (!chip) return;
                e.preventDefault();
                var days = chip.getAttribute('data-next-days');
                selectNextDays(nextActionDays === days ? null : days);
            });
        }
        if (nextDateEl) {
            nextDateEl.addEventListener('input', function() {
                if (nextDateEl.value) selectNextDays(null);
                syncWrapupActions();
            });
        }
        if (handOffPersonEl) handOffPersonEl.addEventListener('change', syncWrapupActions);

        // Wrap-up actions.
        if (saveBtn) {
            saveBtn.addEventListener('click', function(e) { e.preventDefault(); saveWrapup(); });
        }
        if (dismissBtn) {
            dismissBtn.addEventListener('click', function(e) { e.preventDefault(); dismissWrapup(); });
        }

        // ── Audio-device preview / settings (Iteration 7) ──

        // Gear → open the device panel (settings mode; works mid-call too).
        if (settingsBtn) {
            settingsBtn.addEventListener('click', function(e) { e.preventDefault(); openSettings(); });
        }
        // Preview footer.
        if (previewDialBtn) {
            previewDialBtn.addEventListener('click', function(e) { e.preventDefault(); confirmPreview(); });
            // Dial takes focus as the panel opens, often inside the Enter press that opened
            // it; a held key's auto-repeat must not dial past the audio check.
            previewDialBtn.addEventListener('keydown', function(e) { if (e.repeat) e.preventDefault(); });
        }
        if (previewCancelBtn) {
            previewCancelBtn.addEventListener('click', function(e) { e.preventDefault(); cancelPreview(); });
        }
        // Speaker test tone.
        if (speakerTestBtn) {
            speakerTestBtn.addEventListener('click', function(e) {
                e.preventDefault();
                if (!device || !device.audio) return;
                device.audio.speakerDevices.test().catch(function(err) {
                    warn('speaker test failed —', (err && err.message) || err);
                    toast('Could not play test tone', 'error');
                });
            });
        }
        // Device selects — apply + persist on change (defensive, never throws).
        if (micSelect) {
            micSelect.addEventListener('change', function() {
                applyInputDevice(micSelect.value, micSelect);
            });
        }
        if (speakerSelect) {
            speakerSelect.addEventListener('change', function() {
                if (device && device.audio) {
                    applyOutputDevice(device.audio.speakerDevices, speakerSelect.value,
                                      speakerSelect, 'speaker');
                }
            });
        }
        if (ringerSelect) {
            ringerSelect.addEventListener('change', function() {
                if (device && device.audio) {
                    applyOutputDevice(device.audio.ringtoneDevices, ringerSelect.value,
                                      ringerSelect, 'ringer');
                }
            });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    return { init: init, hangup: hangup, onAnswered: onAnswered };
})();

window.Dialer = Dialer;
